import { useOwnershipLabels } from "../../OwnershipLabels";
import { highlightInvalidOwnershipPercentages } from "../../../utils/ownershipPercentageValidation";
import { bindInlineOwnershipPercentage, bindMissingOwnershipPercentage } from "../../../utils/inlineOwnershipPercentage";
import { maximumOwnershipPercentage, parentCarriesPercent } from "../../../utils/editOwnershipPercentage";
import { capitalizeNameInitials, nameFields } from "../../../utils/individualName";
import { builderSeedFromNodes } from "../../../utils/ownershipEdit";
import { companyShareholdersNotice } from "../../../utils/companyReadingPrefill";
import { CLIENT_ENTITY_TYPES, HOLDING_KINDS } from "../../clientEntityType";
import "../css/KycInitialDocumentsModal.css";
// OwnershipTreeUae.jsx
//
// Ownership tree (Mermaid drag & drop) with organization-specific UBO assessment
// - % required ONLY when parent is: Non Quoted Operational Company OR HoldingActive
// - For PIC / Trust / Foundation / HoldingPassive edges are forced 100% for effective-% preview
// - Trust/Foundation roles captured in modal + shown as Mermaid tooltip + shown in UBO preview
//
// NOTE: This file expects CSS at: ../css/OwnershipTreeUae.css

// Send :
// {
//   shareholders: [ ... ],
//   ubos: [ ... ],
//   raw: data,                  // raw API response for the organization jurisdiction
//   ownershipModel: "...",      // e.g. "shareholding_pct" / "shareholding_no_pct" / "trust_foundation"
//   controlOtherThanShareholding: true/false,
//   controlFlags: {
//     pctNotMeaningful: boolean,
//     quotedCompany: boolean,
//     trustFoundation: boolean,
//   },
//   complexStructure: boolean,

//   assistantTree: {            // snapshot for read-only display in KycSectionOwnership
//     mermaid: flowchartString, // string
//     initialAHType: string,    // string
//     nodes: [...],             // array of node objects
//     flowLines: [...],         // array of mermaid edge strings
//   }
// }

// 1) shareholders (array)
// {
//   _rowId: "sh_...",           // generated unique id
//   treeNodeId: "X1",           // stable ref to tree node

//   name: "ACME Holding Ltd",
//   holderType: "Individual" | "Entity",
//   beneficialOwnerType: "Beneficial owner",
//   nationality: "",
//   countryOfResidence: "",

//   ownershipPercent: "25" | "",

//   shareholderEntityType: "PIC" | "HoldingActive" | "HoldingPassive" | "Entity" | ...
//   isPIC: boolean,
//   isHoldingPassive: boolean,
//   isHoldingActive: boolean,
//   isPicOrPassiveHolding: boolean,
// }

// 2) ubos (array)
// {
//   _rowId: "ubo_...",

//   relatedShareholderIdx: "",          // kept but intentionally empty (not stable)

//   relatedShareholderNodeId: "X2",     // direct shareholder node id (stable)
//   relatedShareholderRowId: "sh_...",  // direct shareholder form row id (stable)

//   name: "John Smith",
//   nationality: "",
//   countryOfResidence: "",
//   basisOfControl: "Indirect ownership" | "Trust — Settlor" | ...,
//   effectiveOwnershipPercent: "12.5" | "",
// }

// 3) assistantTree (object snapshot)
// {
//   mermaid: "graph TD; ...",
//   initialAHType: "...",
//   nodes: [ {ID, level, parentIDs, type, percent, name, roles, ...}, ... ],
//   flowLines: [ "X0-->X1(...)", ... ],
// }

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import mermaid from "mermaid";
import { Tooltip } from "primereact/tooltip";
import { AnimatePresence, motion } from "framer-motion";
import CountrySelect from "../../../components/ui/CountrySelect";
import IndividualNamePreview from "../../../components/clients/IndividualNamePreview";
import "../css/OwnershipTreeUae.css";
import { assessUbosFromTree, getUboJurisdiction } from "../../../utils/uboAssessment";
const SHOW_KYC_PREVIEW_CONFIRMATION = false;
const OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED = true;

// Node types that cannot have owners added below them.
const TERMINAL_NODE_TYPES = new Set(["Individual", "Quoted Company", "Trust", "Foundation"]);

// Type of a company shareholder read in the documents (asked before it is placed; a box's type cannot change):
// the same cards as "Organisation type" in the kyc-sent start step, then active or passive for a holding.
const DOCUMENT_COMPANY_CHOICES = ["operating", "holding", "pic", "quoted"]
  .map((value) => CLIENT_ENTITY_TYPES.find((option) => option.value === value));
const DOCUMENT_COMPANY_BUILDER_TYPES = {
  operating: "Non Quoted Operational Company", pic: "PIC", quoted: "Quoted Company",
  holdingActive: "HoldingActive", holdingPassive: "HoldingPassive",
};

const TYPES = [
  { id: "newI", label: "Individual", type: "Individual" },
  { id: "newPIC", label: "PIC", type: "PIC" },
  { id: "newH", label: "Holding", type: "Holding" },
  {
    id: "newNQOC",
    label: "Non Quoted Operational Company",
    type: "Non Quoted Operational Company",
  },
  { id: "newT", label: "Trust", type: "Trust" },
  { id: "newF", label: "Foundation", type: "Foundation" },
  { id: "newQC", label: "Quoted Company", type: "Quoted Company" },
];

function makeInitialNodes() {
  return [
    {
      ID: "X0",
      level: 0,
      parentIDs: [],
      type: "NA",
      typeNo: 0,
      percent: 100,
      boolADE: true,
      name: "",
      roles: null,
    },
  ];
}

function faClassForType(t) {
  if (t === "Individual") return "fa fa-user";
  if (t === "Trust" || t === "Foundation") return "fa fa-bookmark";
  if (t === "HoldingActive" || t === "HoldingPassive") return "fa fa-building";
  return "fa fa-building";
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function toMermaidHtmlLabel(html) {
  return String(html || "")
    .replace(/\r?\n/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, "'");
}

function displayTypeForVisualNode(typeText, isRoot = false) {
  if (isRoot) {
    if (typeText === "PIC") return "Domiciliary Company (PIC)";
    if (typeText === "Non Quoted Operational Company") return "Operating Company";
    if (typeText === "HoldingActive") return "Holding (Active)";
    if (typeText === "HoldingPassive") return "Holding (Passive)";
    return typeText;
  }

  if (typeText === "PIC") return "PIC";
  if (typeText === "Non Quoted Operational Company") return "OpCo";
  if (typeText === "HoldingActive") return "Holding (Active)";
  if (typeText === "HoldingPassive") return "Holding (Passive)";
  return typeText;
}

function entityTypeCapturesIncorporationCountry(typeText) {
  const t = String(typeText || "").trim();
  return (
    t === "PIC" ||
    t === "Non Quoted Operational Company" ||
    t === "Quoted Company" ||
    t === "Holding" ||
    t === "HoldingActive" ||
    t === "HoldingPassive"
  );
}

function modalTitleForPendingType(typeText) {
  if (typeText === "Individual") return "Person";
  if (typeText === "PIC") return "Domiciliary Company (PIC)";
  if (typeText === "Non Quoted Operational Company")
    return "Non Quoted Operational Company";
  if (typeText === "Holding") return "Holding";
  if (typeText === "HoldingActive") return "Active Holding";
  if (typeText === "HoldingPassive") return "Passive Holding";
  if (typeText === "Trust") return "Trust";
  if (typeText === "Foundation") return "Foundation";
  if (typeText === "Quoted Company") return "Quoted Company";
  return typeText || "Entity";
}

function modalSubtitleForPendingType(typeText) {
  if (typeText === "Individual") return "Enter Individual details";
  if (typeText === "PIC") return "Enter PIC details";
  if (typeText === "Non Quoted Operational Company")
    return "Enter Non Quoted Operational Company details";
  if (typeText === "Holding") return "Enter Holding details";
  return `Enter ${typeText || "element"} details`;
}

export function labelForNodeHtml(node, initialAHType) {
  const nm = String(node?.name || "").trim();
  const isRoot = node?.ID === "X0";
  const typeText =
    isRoot
      ? initialAHType || node?.type || "Account Holder"
      : node?.type || "Entity";

  const displayType = displayTypeForVisualNode(typeText, isRoot);

  const safeType = escapeHtml(displayType);
  const safeDisplayName = escapeHtml(toDisplayTitleCase(nm));
  const iconCls = faClassForType(typeText);

  if (isSafariBrowser()) {
    const safeName =
      node?.ID === "X0"
        ? safeDisplayName
        : escapeHtml(toDisplayTitleCase(nm || "(unnamed)"));
    return `
        <span class='otSafariNodeLabel' style='display:inline-block;text-align:center;white-space:nowrap;line-height:1;padding-bottom:0;vertical-align:top;'>
          <span class='otSafariNodeMain' style='display:flex;align-items:center;justify-content:center;gap:5px;font-size:14px;font-weight:700;color:#3f4d5a;line-height:.9;'>
            <i class='${iconCls}' style='font-size:13px;line-height:1;'></i>
            <span>${safeType}</span>
          </span>
          <span class='otSafariNodeSub' style='display:block;margin-top:10px;font-size:12px;font-weight:400;color:#6c7278;line-height:.7;'>${safeName}</span>
        </span>
      `;
  }

  if (node?.ID === "X0") {
    return `
        <span style='display:inline-block;padding:5px 10px;text-align:center;white-space:nowrap;line-height:1;'>
          <span style='display:inline-block;white-space:nowrap;font-weight:600;font-size:15px;line-height:15px;'>
            <i class='${iconCls}' style='display:inline-block;font-size:15px;line-height:15px;margin-right:6px;vertical-align:-1px;'></i><span style='line-height:15px;'>${safeType}</span>
          </span>
          <span style='display:block;margin-top:12px;font-size:12px;opacity:.85;white-space:nowrap;line-height:12px;'>${safeDisplayName}</span>
        </span>
      `;
  }

  const safeName = escapeHtml(toDisplayTitleCase(nm || "(unnamed)"));
  return `
      <div style='padding:0px 10px; line-height:1.05; text-align:center;'>
        <div style='display:flex;align-items:center;justify-content:center;gap:6px;white-space:nowrap;'>
          <i class='${iconCls}' style='font-size:15px;line-height:1;'></i>
          <span style='font-weight:600;font-size:15px;line-height:1;'>${safeType}</span>
        </div>
        <div style='margin-top:8px;font-size:12px;opacity:.85;white-space:nowrap;line-height:1;'>${safeName}</div>
      </div>
    `;
}

function isSafariBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = String(navigator.userAgent || "");
  const vendor = String(navigator.vendor || "");
  return (
    /Safari/i.test(ua) &&
    /Apple/i.test(vendor) &&
    !/Chrome|Chromium|CriOS|FxiOS|Firefox|Edg|EdgiOS|OPR|OPT|SamsungBrowser|Android/i.test(
      ua
    )
  );
}

function iconTokenFromHtmlLabel(html) {
  const source = String(html || "");
  if (/fa-user/i.test(source)) return "fa:fa-user";
  if (/fa-bookmark/i.test(source)) return "fa:fa-bookmark";
  return "fa:fa-building";
}

function iconClassFromHtmlLabel(html) {
  const source = String(html || "");
  if (/fa-user/i.test(source)) return "fa fa-user";
  if (/fa-bookmark/i.test(source)) return "fa fa-bookmark";
  return "fa fa-building";
}

function escapePlainMermaidLabel(value) {
  return String(value ?? "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, "'")
    .replace(/"/g, "'");
}

function toDisplayTitleCase(value) {
  return String(value || "").replace(/\p{L}[\p{L}'-]*/gu, (word) => {
    const [first = "", ...rest] = Array.from(word);
    return `${first.toLocaleUpperCase()}${rest.join("")}`;
  });
}

function extractHtmlTreeLabelLines(label) {
  const raw = String(label || "");

  if (/fa:fa-/i.test(raw)) {
    const withoutIcon = raw.replace(/fa:fa-[\w-]+/i, "").trim();
    return withoutIcon
      .split(/<br\s*\/?>/i)
      .map((part) =>
        part.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
      )
      .filter(Boolean);
  }

  if (typeof DOMParser !== "undefined") {
    try {
      const doc = new DOMParser().parseFromString(
        `<div>${raw}</div>`,
        "text/html"
      );
      const safariLabel = doc.body.querySelector(".otSafariNodeLabel");
      if (safariLabel) {
        const main = String(
          safariLabel.querySelector(".otSafariNodeMain")?.textContent || ""
        )
          .replace(/\s+/g, " ")
          .trim();
        const sub = String(
          safariLabel.querySelector(".otSafariNodeSub")?.textContent || ""
        )
          .replace(/\s+/g, " ")
          .trim();
        return [main, sub].filter(Boolean);
      }

      const paragraph = doc.body.querySelector("p");
      if (paragraph) {
        const clone = paragraph.cloneNode(true);
        clone.querySelectorAll("i").forEach((icon) => icon.remove());
        const lines = String(clone.innerHTML || "")
          .split(/<br\s*\/?>/i)
          .map((part) =>
            part.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
          )
          .filter(Boolean);
        if (lines.length) return lines;
      }

      const leafLines = Array.from(doc.body.querySelectorAll("div"))
        .filter((el) => !el.querySelector("div"))
        .map((el) => String(el.textContent || "").replace(/\s+/g, " ").trim())
        .filter(Boolean);
      if (leafLines.length) return leafLines;
    } catch (_) {}
  }

  return raw
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .split(/\n/)
    .map((part) => part.replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

function htmlTreeLabelToSafariHtmlLabel(label) {
  const raw = String(label || "");

  const iconCls = iconClassFromHtmlLabel(raw);
  const lines = extractHtmlTreeLabelLines(raw);
  const typeLine = lines[0] || "Entity";
  const nameLine = lines.length > 1 ? toDisplayTitleCase(lines[lines.length - 1]) : "";

  return `
    <span class='otSafariNodeLabel' style='display:inline-block;text-align:center;white-space:nowrap;line-height:1;padding-bottom:0;vertical-align:top;'>
      <span class='otSafariNodeMain' style='display:flex;align-items:center;justify-content:center;gap:5px;font-size:14px;font-weight:700;color:#3f4d5a;line-height:.9;'>
        <i class='${iconCls}' style='font-size:13px;line-height:1;'></i>
        <span>${escapeHtml(typeLine)}</span>
      </span>
      <span class='otSafariNodeSub' style='display:block;margin-top:10px;font-size:12px;font-weight:400;color:#6c7278;line-height:.7;'>${escapeHtml(
        nameLine
      )}</span>
    </span>
  `
    .replace(/\r?\n/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/"/g, "'");
}

function htmlTreeLabelToPlainMermaidLabel(label) {
  const raw = String(label || "");
  if (!/<[a-z][\s\S]*>/i.test(raw)) return raw;

  const iconToken = iconTokenFromHtmlLabel(raw);
  if (typeof DOMParser !== "undefined") {
    try {
      const doc = new DOMParser().parseFromString(
        `<div>${raw}</div>`,
        "text/html"
      );
      const paragraph = doc.body.querySelector("p");
      let lines = [];

      if (paragraph) {
        const clone = paragraph.cloneNode(true);
        clone.querySelectorAll("i").forEach((icon) => icon.remove());
        lines = String(clone.innerHTML || "")
          .split(/<br\s*\/?>/i)
          .map((part) =>
            part.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
          )
          .filter(Boolean);
      }

      if (!lines.length) {
        lines = Array.from(doc.body.querySelectorAll("div"))
          .filter((el) => !el.querySelector("div"))
          .map((el) => String(el.textContent || "").replace(/\s+/g, " ").trim())
          .filter(Boolean);
      }

      const uniqueLines = lines.filter(
        (line, idx) => lines.indexOf(line) === idx
      );
      const typeLine = uniqueLines[0] || "Entity";
      const nameLine = uniqueLines[uniqueLines.length - 1] || "";
      return `${iconToken} ${escapePlainMermaidLabel(typeLine)}<br/>${escapePlainMermaidLabel(
        nameLine
      )}`;
    } catch (_) {}
  }

  const fallbackText = raw
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return `${iconToken} ${escapePlainMermaidLabel(fallbackText)}`;
}

function convertHtmlTreeLabelsToPlainMermaid(source) {
  return String(source || "").replace(
    /(\b[A-Za-z_][\w-]*\(")([\s\S]*?)("\))/g,
    (match, start, label, end) => {
      if (!/<[a-z][\s\S]*>/i.test(label)) return match;
      return `${start}${htmlTreeLabelToPlainMermaidLabel(label)}${end}`;
    }
  );
}

function convertHtmlTreeLabelsToSafariHtmlMermaid(source) {
  return String(source || "").replace(
    /(\b[A-Za-z_][\w-]*\(")([\s\S]*?)("\))/g,
    (match, start, label, end) =>
      `${start}${htmlTreeLabelToSafariHtmlLabel(label)}${end}`
  );
}

function allocateNewNodeIds(newType, countersRef) {
  const c = countersRef.current;
  c.ElementNo += 1;
  const newId = `X${c.ElementNo}`;

  let typeNo = 0;
  let boolADE = true;

  if (newType === "Individual") {
    c.NoIndividual += 1;
    typeNo = c.NoIndividual;
    boolADE = false;
  } else if (newType === "PIC") {
    c.NoPIC += 1;
    typeNo = c.NoPIC;
  } else if (newType === "Holding") {
    c.NoHolding += 1;
    typeNo = c.NoHolding;
  } else if (newType === "Non Quoted Operational Company") {
    c.NoNQCompany += 1;
    typeNo = c.NoNQCompany;
  } else if (newType === "Quoted Company") {
    c.NoQCompany += 1;
    typeNo = c.NoQCompany;
  } else if (newType === "Trust") {
    c.NoTrust += 1;
    typeNo = c.NoTrust;
  } else if (newType === "Foundation") {
    c.NoFoundation += 1;
    typeNo = c.NoFoundation;
  }

  return { newId, typeNo, boolADE };
}

function extractNodeIdFromG(g) {
  const titleEl = g.querySelector("title");
  const title = (titleEl?.textContent || "").trim();
  if (/^X\d+$/.test(title)) return title;

  const rawId = g.getAttribute("id") || "";
  const m = rawId.match(/(X\d+)/);
  if (m?.[1]) return m[1];

  return null;
}

function emptyTrustRoles() {
  return {
    settlorName: "",
    settlorNationality: "",
    settlorDomicile: "",
    trusteeKind: "company",
    trusteeName: "",
    trusteeCountry: "",
    trusteeNationality: "",
    trusteeDomicile: "",
    beneficiariesAreClass: false,
    beneficiaries: [""],
    hasProtector: false,
    protectorKind: "individual",
    protectorName: "",
    protectorCountry: "",
    protectorNationality: "",
    protectorDomicile: "",
  };
}

function emptyFoundationRoles() {
  return {
    founderName: "",
    founderNationality: "",
    founderDomicile: "",
    councilKind: "company",
    councilName: "",
    councilCountry: "",
    councilNationality: "",
    councilDomicile: "",
    beneficiariesAreClass: false,
    beneficiaries: [""],
    hasGuardian: false,
    guardianKind: "individual",
    guardianName: "",
    guardianCountry: "",
    guardianNationality: "",
    guardianDomicile: "",
  };
}

// Saved node roles back into the details-form state, to edit an existing trust/foundation node.
function namedBeneficiariesForForm(beneficiaries) {
  const list = beneficiaries?.kind === "named" && Array.isArray(beneficiaries.list) ? beneficiaries.list : [];
  return list.length
    ? list.map((b) => ({ ...nameFields(b), name: String(b?.name || ""), nationality: String(b?.nationality || ""), domicile: String(b?.domicile || "") }))
    : [""];
}

function partyForForm(prefix, party, withKind) {
  const p = party || {};
  return {
    [`${prefix}Name`]: String(p.name || ""),
    [`${prefix}NameParts`]: nameFields(p),
    [`${prefix}Nationality`]: String(p.nationality || ""),
    [`${prefix}Domicile`]: String(p.domicile || ""),
    ...(withKind ? {
      [`${prefix}Kind`]: p.kind === "company" ? "company" : "individual",
      [`${prefix}Country`]: String(p.countryOfIncorporation || ""),
    } : {}),
  };
}

export function trustRolesFromNode(roles) {
  const r = roles || {};
  return {
    ...emptyTrustRoles(),
    ...partyForForm("settlor", r.settlor, false),
    ...partyForForm("trustee", r.trustee, true),
    ...(r.trustee ? {} : { trusteeKind: "company" }),
    beneficiariesAreClass: r.beneficiaries?.kind === "class",
    beneficiaries: namedBeneficiariesForForm(r.beneficiaries),
    hasProtector: !!r.protector,
    ...(r.protector ? partyForForm("protector", r.protector, true) : {}),
  };
}

export function foundationRolesFromNode(roles) {
  const r = roles || {};
  return {
    ...emptyFoundationRoles(),
    ...partyForForm("founder", r.founder, false),
    ...partyForForm("council", r.council, true),
    ...(r.council ? {} : { councilKind: "company" }),
    beneficiariesAreClass: r.beneficiaries?.kind === "class",
    beneficiaries: namedBeneficiariesForForm(r.beneficiaries),
    hasGuardian: !!r.guardian,
    ...(r.guardian ? partyForForm("guardian", r.guardian, true) : {}),
  };
}

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Incoming flow line of a node: `PARENT-->ID(...)` or `PARENT-->|pct%|ID(...)`. */
export function isIncomingLineOf(line, parentId, nodeId) {
  return new RegExp(`^${escapeRegExp(parentId)}-->(\\|[^|]*\\|)?${escapeRegExp(nodeId)}\\(`).test(String(line || "").trim());
}

/** Target node ID of a flow line, or "" for anything else. */
// Identity of a controlling-person row built from the diagram: its own box, or for a
// trust/foundation party, the party's role in that arrangement's box. Never the name alone,
// so two people with the same name (or one person in two branches) stay separate rows.
export function uboRowKey({ treeNodeId, subjectNodeId, name, basisOfControl }) {
  const norm = (value) => String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
  const nodeId = String(treeNodeId || "").trim();
  if (nodeId) return `node:${nodeId}`;
  return `party:${String(subjectNodeId || "").trim()}|${norm(name)}|${norm(basisOfControl)}`;
}

export function flowLineTarget(line) {
  const m = String(line || "").trim().match(/^[\w-]+\s*-->(?:\|[^|]*\|)?\s*([\w-]+)\s*\(/);
  return m ? m[1] : "";
}

function normalizeTrustBeneficiary(entry) {
  if (entry && typeof entry === "object") {
    return {
      ...nameFields(entry),
      name: String(entry.name || ""),
      nationality: String(entry.nationality || ""),
      domicile: String(entry.domicile || ""),
    };
  }
  return {
    name: String(entry || ""),
    nationality: "",
    domicile: "",
  };
}

function normalizeFoundationBeneficiary(entry) {
  if (entry && typeof entry === "object") {
    return {
      ...nameFields(entry),
      name: String(entry.name || ""),
      nationality: String(entry.nationality || ""),
      domicile: String(entry.domicile || ""),
    };
  }
  return {
    name: String(entry || ""),
    nationality: "",
    domicile: "",
  };
}

function boldLabel(s) {
  const map = {
    Settlor: "𝗦𝗲𝘁𝘁𝗹𝗼𝗿",
    Trustee: "𝗧𝗿𝘂𝘀𝘁𝗲𝗲",
    Beneficiaries: "𝗕𝗲𝗻𝗲𝗳𝗶𝗰𝗶𝗮𝗿𝗶𝗲𝘀",
    Protector: "𝗣𝗿𝗼𝘁𝗲𝗰𝘁𝗼𝗿",
    Founder: "𝗙𝗼𝘂𝗻𝗱𝗲𝗿",
    Council: "𝗖𝗼𝘂𝗻𝗰𝗶𝗹",
    Guardian: "𝗚𝘂𝗮𝗿𝗱𝗶𝗮𝗻",
  };
  return map[s] || s;
}

function trustTooltipText(node) {
  const r = node?.roles;
  if (!r || r.kind !== "trust") return `Trust roles\nNo roles captured`;

  const settlor = String(r.settlor?.name || "—");
  const trusteeName = r.trustee?.name || "—";
  const trusteeKind = r.trustee?.kind || "—";
  const trustee = `${trusteeName} (${trusteeKind})`;

  let beneficiaries = "—";
  if (r.beneficiaries?.kind === "class") beneficiaries = "Class (unnamed)";
  if (r.beneficiaries?.kind === "named") {
    const list = (r.beneficiaries.list || [])
      .map((x) => String(x?.name || "").trim())
      .filter(Boolean);
    beneficiaries = list.length ? list.map((n) => `• ${n}`).join("\n") : "—";
  }

  const protector = r.protector
    ? `${String(r.protector.kind)} — ${String(r.protector.name || "—")}`
    : "None";

  return `${boldLabel("Settlor")}: ${settlor}
  ${boldLabel("Trustee")}: ${trustee}
  ${boldLabel("Beneficiaries")}:
  ${beneficiaries}
  ${boldLabel("Protector")}: ${protector}`;
}

function foundationTooltipText(node) {
  const r = node?.roles;
  if (!r || r.kind !== "foundation")
    return `Foundation roles\nNo roles captured`;

  const founder = String(r.founder?.name || "—");
  const council = `${String(r.council?.kind || "—")} — ${String(
    r.council?.name || "—"
  )}`;

  let beneficiaries = "—";
  if (r.beneficiaries?.kind === "class") beneficiaries = "Class (unnamed)";
  if (r.beneficiaries?.kind === "named") {
    const list = (r.beneficiaries.list || [])
      .map((x) => String(x?.name || "").trim())
      .filter(Boolean);
    beneficiaries = list.length ? list.join("\n") : "—";
  }

  const guardian = r.guardian
    ? `${String(r.guardian.kind)} — ${String(r.guardian.name || "—")}`
    : "None";

  return `Foundation roles
  ${boldLabel("Founder")}: ${founder}
  ${boldLabel("Council")}: ${council}
  ${boldLabel("Beneficiaries")}:
  ${beneficiaries}
  ${boldLabel("Guardian")}: ${guardian}`;
}

function formatPct(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

/* -------------------- roles formatter for preview -------------------- */

function renderTrustRolesInline(r) {
  if (!r || r.kind !== "trust") return null;

  const settlor = String(r?.settlor?.name || "—");
  const trustee = `${String(r?.trustee?.name || "—")} (${String(
    r?.trustee?.kind || "—"
  )})`;

  let beneficiaries = "—";
  if (r?.beneficiaries?.kind === "class") beneficiaries = "Class (unnamed)";
  if (r?.beneficiaries?.kind === "named") {
    const list = (r?.beneficiaries?.list || [])
      .map((x) => String(x?.name || "").trim())
      .filter(Boolean);
    beneficiaries = list.length ? list.join(", ") : "—";
  }

  const protector = r?.protector
    ? `${String(r?.protector?.name || "—")} (${String(
        r?.protector?.kind || "—"
      )})`
    : "None";

  return (
    <div style={{ marginTop: 6, paddingLeft: 2, lineHeight: 1.45 }}>
      <div>
        <span style={{ fontWeight: 600 }}>{boldLabel("Settlor")}:</span>{" "}
        {settlor}
      </div>
      <div>
        <span style={{ fontWeight: 600 }}>{boldLabel("Trustee")}:</span>{" "}
        {trustee}
      </div>
      <div>
        <span style={{ fontWeight: 600 }}>{boldLabel("Beneficiaries")}:</span>{" "}
        {beneficiaries}
      </div>
      <div>
        <span style={{ fontWeight: 600 }}>{boldLabel("Protector")}:</span>{" "}
        {protector}
      </div>
    </div>
  );
}

function renderFoundationRolesInline(r) {
  if (!r || r.kind !== "foundation") return null;

  const founder = String(r?.founder?.name || "—");
  const council = `${String(r?.council?.name || "—")} (${String(
    r?.council?.kind || "—"
  )})`;

  let beneficiaries = "—";
  if (r?.beneficiaries?.kind === "class") beneficiaries = "Class (unnamed)";
  if (r?.beneficiaries?.kind === "named") {
    const list = (r?.beneficiaries?.list || [])
      .map((x) => String(x?.name || "").trim())
      .filter(Boolean);
    beneficiaries = list.length ? list.join(", ") : "—";
  }

  const guardian = r?.guardian
    ? `${String(r?.guardian?.name || "—")} (${String(
        r?.guardian?.kind || "—"
      )})`
    : "None";

  return (
    <div style={{ marginTop: 6, paddingLeft: 2, lineHeight: 1.45 }}>
      <div>
        <span style={{ fontWeight: 600 }}>{boldLabel("Founder")}:</span>{" "}
        {founder}
      </div>
      <div>
        <span style={{ fontWeight: 600 }}>{boldLabel("Council")}:</span>{" "}
        {council}
      </div>
      <div>
        <span style={{ fontWeight: 600 }}>{boldLabel("Beneficiaries")}:</span>{" "}
        {beneficiaries}
      </div>
      <div>
        <span style={{ fontWeight: 600 }}>{boldLabel("Guardian")}:</span>{" "}
        {guardian}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* --------------------------  EXPORT FUNCTION  ----------------------------- */
/* -------------------------------------------------------------------------- */

function isOwnershipAssistantFormEntryTarget(target) {
  const el = target instanceof Element ? target : null;
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  if (tag === "input" || tag === "select" || tag === "textarea") return true;
  return !!el.closest(
    [
      ".rs__control",
      ".rs__menu",
      ".rs__menu-list",
      ".rs__option",
      ".rs__input-container",
      "[role='combobox']",
      "[role='listbox']",
      "[role='option']",
    ].join(",")
  );
}

function ownershipAssistantEntryField(target) {
  const el = target instanceof Element ? target : null;
  if (!el) return "";
  return String(el.closest("[data-ot-entry-field]")?.dataset?.otEntryField || "");
}

// Opening the builder: the top card fades in, then the diagram (staged).
const BUILDER_TOP_FADE_S = 0.3;

// Height that follows its content, animated (the diagram below moves smoothly when the top card changes).
// flow-root: the content's margins count in the measured height.
function MeasuredHeight({ children }) {
  const inner = useRef(null);
  const [height, setHeight] = useState("auto");
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => setHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <motion.div initial={false} animate={{ height }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: "hidden" }}>
      <div ref={inner} style={{ display: "flow-root" }}>{children}</div>
    </motion.div>
  );
}

export default function OwnershipTreeUae({
  customerName,
  separateNames = false,
  orgJurisdiction = null,
  onClose,
  onUboResult,
  onApplyKycUpdate,
  initialAccountHolderType = "",
  initialAccountHolderTrustNameKind = "",
  clientMode = false,
  // "Edit diagram": the current structure to start from (see builderSeedFromNodes).
  initialStructure = null,
  // Direct shareholders read in the company documents (kyc-sent, a company among them): the type of each company
  // is asked first, then they are placed under the client and the client continues.
  // [{ kind: "company", name, country, percent } | { kind: "individual", name, nationality, countryOfResidence, percent }]
  documentShareholders = null,
}) {
  const { ownershipLabel, ownershipDiagram } = useOwnershipLabels(orgJurisdiction);
  const graphWrapRef = useRef(null);
  const tooltipRef = useRef(null);

  const [initialAHType, setInitialAHType] = useState("");
  const [paletteVisible, setPaletteVisible] = useState(false);

  const [nodes, setNodes] = useState(makeInitialNodes);
  const [flowLines, setFlowLines] = useState([]);

  const [kycPreviewOpen, setKycPreviewOpen] = useState(false);
  const [kycPreviewPayload, setKycPreviewPayload] = useState(null);
  const [kycPreviewResult, setKycPreviewResult] = useState(null);
  const [kycPreviewLoading, setKycPreviewLoading] = useState(false);
  const [kycPreviewErr, setKycPreviewErr] = useState("");

  const countersRef = useRef({
    ElementNo: 0,
    NoIndividual: 0,
    NoPIC: 0,
    NoHolding: 0,
    NoNQCompany: 0,
    NoQCompany: 0,
    NoTrust: 0,
    NoFoundation: 0,
  });

  /* ----------------------------  useState --------------------------------- */

  // Modal state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsName, setDetailsName] = useState("");
  const [detailsNameParts, setDetailsNameParts] = useState({});
  const [detailsPct, setDetailsPct] = useState("");
  const [pctError, setPctError] = useState(""); // ✅ inline error
  const [detailsNationality, setDetailsNationality] = useState("");
  const [detailsDomicile, setDetailsDomicile] = useState("");
  const [detailsIncorporationCountry, setDetailsIncorporationCountry] =
    useState("");
  const [countryDetailsError, setCountryDetailsError] = useState("");
  const pendingAddRef = useRef(null);

  const [trustRoles, setTrustRoles] = useState(emptyTrustRoles);
  const [foundationRoles, setFoundationRoles] = useState(emptyFoundationRoles);
  const [accountHolderTrustNameKind, setAccountHolderTrustNameKind] =
    useState("");

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [nameError, setNameError] = useState("");

  // Holding classification (stored as node.type = HoldingActive/HoldingPassive)
  const [holdingChoice, setHoldingChoice] = useState(""); // "active" | "passive" | ""
  const [holdingTouched, setHoldingTouched] = useState(false);

  const [uboResult, setUboResult] = useState(null);
  const [callingApi, setCallingApi] = useState(false);

  // Submit validation alert
  const [submitAlertOpen, setSubmitAlertOpen] = useState(false);
  const [submitAlertLeaves, setSubmitAlertLeaves] = useState([]); // [{id,type,name}]
  const [submitAlertMode, setSubmitAlertMode] = useState("invalidLeaves");
  const trimmedCustomerName = String(customerName || "").trim();

  /* ------------------------------  useMemo  ----------------------------------- */

  const nodeById = useMemo(() => {
    const m = new Map();
    (nodes || []).forEach((n) => m.set(n.ID, n));
    return m;
  }, [nodes]);

  // For display in preview
  const idToLabel = useCallback(
    (id) => {
      const n = nodeById.get(id);
      if (!n) return `${id} (not found)`;
      const nm = String(n.name || "").trim() || "(unnamed)";
      return `${id} — ${nm}`;
    },
    [nodeById]
  );

  const effectivePctForNode = useCallback(
    (nodeId) => {
      const start = nodeById.get(nodeId);
      if (!start) return null;
      if (orgJurisdiction !== null && getUboJurisdiction(orgJurisdiction) === "switzerland") {
        const pct = start.percent;
        return pct !== "" && pct != null && Number.isFinite(Number(pct)) ? Number(pct) : null;
      }

      // chain: [nodeId, parent, grandparent, ...]
      const chain = [
        nodeId,
        ...(Array.isArray(start.parentIDs) ? start.parentIDs : []),
      ];

      let eff = 1;

      for (let i = 0; i < chain.length - 1; i++) {
        const childId = chain[i];
        const parentId = chain[i + 1];

        const child = nodeById.get(childId);
        const parent = nodeById.get(parentId);
        if (!child || !parent) return null;

        const parentType = parent.type;

        // ✅ forced 100% edges
        const edgeIsForced100 =
          parentType === "PIC" ||
          parentType === "Trust" ||
          parentType === "Foundation" ||
          parentType === "HoldingPassive";

        let edgePct = 100;

        if (!edgeIsForced100) {
          const pct = Number(child.percent);
          if (!Number.isFinite(pct) || pct < 0 || pct > 100) return null;
          edgePct = pct;
        }

        eff *= edgePct / 100;
      }

      return eff * 100;
    },
    [nodeById, orgJurisdiction]
  );

  // helper to check that account holder name has been indicated
  function requireCustomerNameOrAlert() {
    const cn = String(customerName || "").trim();
    if (!cn) {
      alert("Missing client name. Please fill it first.");
      return null;
    }
    return cn;
  }

  // helper to check that all branches have valid terminal leaves
  function findInvalidTerminalLeaves(nodes, initialAHType, customerName) {
    const list = Array.isArray(nodes) ? nodes : [];

    // children count by parentID
    const childCount = new Map();
    for (const n of list) {
      const pid =
        Array.isArray(n?.parentIDs) && n.parentIDs.length
          ? n.parentIDs[0]
          : null;
      if (!pid) continue;
      childCount.set(pid, (childCount.get(pid) || 0) + 1);
    }

    const allowedTerminal = new Set([
      "Individual",
      "Trust",
      "Foundation",
      "Quoted Company",
    ]);

    const invalid = [];

    // if account holder (X0) has NO children, it’s a terminal leaf too
    const x0Children = childCount.get("X0") || 0;
    if (x0Children === 0) {
      const t = String(initialAHType || "").trim();
      if (!allowedTerminal.has(t)) {
        invalid.push({
          id: "X0",
          type: t || "Account Holder",
          name: String(customerName || "").trim() || "(unnamed)",
        });
      }
    }

    // leaf = node that has no children (excluding X0)
    const leaves = list.filter((n) => {
      if (!n || n.ID === "X0") return false;
      return (childCount.get(n.ID) || 0) === 0;
    });

    // invalid leaf = leaf not in allowed terminal types
    invalid.push(
      ...leaves
        .filter((n) => !allowedTerminal.has(String(n.type || "").trim()))
        .map((n) => ({
          id: n.ID,
          type: String(n.type || "").trim(),
          name: String(n.name || "").trim() || "(unnamed)",
        }))
    );

    return invalid;
  }

  // ✅ direct children helper
  const directChildrenOf = useCallback(
    (parentId) => {
      if (!parentId) return [];
      return (nodes || []).filter((n) => {
        const pid =
          Array.isArray(n.parentIDs) && n.parentIDs.length
            ? n.parentIDs[0]
            : null;
        return pid === parentId;
      });
    },
    [nodes]
  );

  // ✅ remaining capacity per parent
  const remainingPctForParent = useCallback(
    (parentId, excludeId = "") => {
      if (!parentId) return 100;

      // When editing a node, its own current % is available again.
      const children = directChildrenOf(parentId).filter((child) => child.ID !== excludeId);

      let used = 0;
      for (const child of children) {
        const v = Number(child.percent);
        if (Number.isFinite(v) && v > 0) used += v;
      }

      let left = 100 - used;
      if (left < 0) left = 0;
      if (left > 100) left = 100;
      return Math.round(left * 100) / 100;
    },
    [directChildrenOf]
  );

  const leftForPendingParent = useMemo(() => {
    const p = pendingAddRef.current;
    if (!p?.requiresPct) return null;
    return remainingPctForParent(p.parentID, p.editId);
  }, [detailsOpen, detailsPct, remainingPctForParent]);

  /* ---------------------------  useEffect ---------------------------------- */

  // Prevent scrolling behind modal without repositioning the underlying page.
  useLayoutEffect(() => {
    if (!detailsOpen) return;

    const body = document.body;
    const html = document.documentElement;

    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflowY: body.style.overflowY,
      paddingRight: body.style.paddingRight,
    };

    const scrollbarW = window.innerWidth - html.clientWidth;
    if (scrollbarW > 0) body.style.paddingRight = `${scrollbarW}px`;

    html.style.overflow = "hidden";
    body.style.overflowY = "hidden";

    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflowY = prev.bodyOverflowY;
      body.style.paddingRight = prev.paddingRight;
    };
  }, [detailsOpen]);

  // Mermaid init
  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "loose",
      flowchart: { htmlLabels: true, wrappingWidth: 1000, useMaxWidth: true },
    });
  }, []);

  const flowchartString = useMemo(() => {
    if (!initialAHType) return "graph TD;";

    const ahNode = nodes.find((n) => n.ID === "X0") || {
      ID: "X0",
      name: "",
      type: initialAHType,
    };

    const ahName = String(ahNode?.name || "").trim();
    if (!ahName) return "graph TD;";

    const ahLabelHtml = labelForNodeHtml(ahNode, initialAHType);
    const base = `graph TD;X0("${toMermaidHtmlLabel(ahLabelHtml)}")`;
    const body = flowLines.length ? `;${flowLines.join(";")}` : "";
    const styleTail = `\nclassDef default fill:#BAD9CB,stroke:#BAD9CB,stroke-width:0px,color:#484848
  linkStyle default stroke-width:2px,fill:none,stroke:#B0B0B0`;

    return `${base}${body}${styleTail}`;
  }, [initialAHType, nodes, flowLines]);

  const onDragStart = useCallback((e, id) => {
    e.dataTransfer.setData("text/plain", id);
    e.dataTransfer.effectAllowed = "copy";
  }, []);

  const resetAll = useCallback(() => {
    setInitialAHType("");
    setPaletteVisible(false);
    setDetailsOpen(false);
    pendingAddRef.current = null;

    countersRef.current = {
      ElementNo: 0,
      NoIndividual: 0,
      NoPIC: 0,
      NoHolding: 0,
      NoNQCompany: 0,
      NoQCompany: 0,
      NoTrust: 0,
      NoFoundation: 0,
    };

    setNodes(makeInitialNodes());
    setFlowLines([]);
    setUboResult(null);

    setTrustRoles(emptyTrustRoles());
    setFoundationRoles(emptyFoundationRoles());

    setHoldingChoice("");
    setHoldingTouched(false);

    setDetailsName(""); setDetailsNameParts({});
    setDetailsPct("");
    setPctError("");
    setDetailsNationality("");
    setDetailsDomicile("");
    setDetailsIncorporationCountry("");
    setCountryDetailsError("");

    if (graphWrapRef.current) graphWrapRef.current.innerHTML = "";
  }, []);

  const startWithAH = useCallback(
    (type, options = {}) => {
      const cn = requireCustomerNameOrAlert();
      if (!cn) return;
      const normalizedType = String(type || "").trim();

      // reset counters + base nodes
      countersRef.current = {
        ElementNo: 0,
        NoIndividual: 0,
        NoPIC: 0,
        NoHolding: 0,
        NoNQCompany: 0,
        NoQCompany: 0,
        NoTrust: 0,
        NoFoundation: 0,
      };

      setNodes(() => {
        const base = makeInitialNodes();
        base[0].name = cn; // ✅ X0 name always known
        base[0].roles = null;
        base[0].type = normalizedType; // temporary; for Holding we’ll override after choice
        return base;
      });

      setFlowLines([]);
      setUboResult(null);
      setPaletteVisible(true);

      // reset modal fields
      setSubmitAttempted(false);
      setNameError("");
      setDetailsName(""); setDetailsNameParts({});
      setDetailsPct("");
      setPctError("");
      setDetailsNationality("");
      setDetailsDomicile("");
      setDetailsIncorporationCountry("");
      setCountryDetailsError("");
      setTrustRoles(emptyTrustRoles());
      setFoundationRoles(emptyFoundationRoles());
      setAccountHolderTrustNameKind(
        String(options?.trustNameKind || "").trim().toLowerCase()
      );
      setHoldingChoice("");
      setHoldingTouched(false);

      // ✅ Client wrapper can preselect the holding subtype before opening builder.
      if (
        normalizedType === "HoldingActive" ||
        normalizedType === "HoldingPassive"
      ) {
        setInitialAHType(normalizedType);
        setDetailsOpen(false);
        pendingAddRef.current = null;
        return;
      }

      if (normalizedType === "Quoted Company") {
        setInitialAHType(normalizedType);
        setPaletteVisible(false);
        setDetailsOpen(false);
        pendingAddRef.current = null;

        onApplyKycUpdate?.({
          initialAHType: normalizedType,
          shareholders: [],
          ubos: [],
          arrangements: [],
          raw: null,
          ownershipModel: "shareholding_no_pct",
          controlOtherThanShareholding: false,
          controlFlags: {
            pctNotMeaningful: false,
            quotedCompany: true,
            trustFoundation: false,
          },
          complexStructure: false,
          assistantTree: null,
          accountHolderTrustResolution: null,
        });
        onClose?.();
        return;
      }

      // ✅ Special: Account Holder = Holding => open modal ONLY to choose holding type
      if (normalizedType === "Holding") {
        setInitialAHType("Holding"); // temporary until user selects active/passive

        pendingAddRef.current = {
          newType: "Holding",
          parentID: null,
          parentType: null,
          requiresPct: false,
          draftId: "X0",
          draftTypeNo: 0,
          draftBoolADE: true,
          isAccountHolder: true, // ✅ flag
        };

        setDetailsName(cn); // name known
        setDetailsOpen(true); // open modal
        return;
      }

      // normal AH start
      setInitialAHType(normalizedType);

      // ✅ For Trust/Foundation as Account Holder, we MUST capture roles
      if (normalizedType === "Trust" || normalizedType === "Foundation") {
        pendingAddRef.current = {
          newType: normalizedType,
          parentID: null,
          parentType: null,
          requiresPct: false,
          draftId: "X0",
          draftTypeNo: 0,
          draftBoolADE: true,
          isAccountHolder: true, // ✅ flag
        };

        // For account-holder trust/foundation flows, keep the editable name
        // field empty until the specific flow decides what should be derived.
        setDetailsName(""); setDetailsNameParts({});
        setDetailsPct("");
        setPctError("");
        setDetailsNationality("");
        setDetailsDomicile("");
        setDetailsIncorporationCountry("");
        setCountryDetailsError("");
        setTrustRoles(emptyTrustRoles());
        setFoundationRoles(emptyFoundationRoles());
        setHoldingChoice("");
        setHoldingTouched(false);

        setDetailsOpen(true);
        return;
      }

      // ✅ no modal for other X0 types
      setDetailsOpen(false);
      pendingAddRef.current = null;

      // ✅ no modal for other X0 types
      setDetailsOpen(false);
      pendingAddRef.current = null;
    },
    [customerName, onApplyKycUpdate, onClose]
  );

  // Start from an existing structure (Edit diagram, or the shareholders read in the company documents): boxes,
  // % links and trust/foundation roles.
  const applySeed = useCallback((seed) => {
    const seedNodes = seed.nodes.map((n) => ({ ...n }));
    const root = seedNodes.find((n) => n.ID === "X0");
    if (root) root.name = String(customerName || root.name || "").trim();
    const typeCounts = seed.typeCounts || {};
    countersRef.current = {
      ElementNo: Number(seed.elementCount) || seedNodes.length,
      NoIndividual: typeCounts.Individual || 0,
      NoPIC: typeCounts.PIC || 0,
      NoHolding: (typeCounts.HoldingActive || 0) + (typeCounts.HoldingPassive || 0),
      NoNQCompany: typeCounts["Non Quoted Operational Company"] || 0,
      NoQCompany: typeCounts["Quoted Company"] || 0,
      NoTrust: typeCounts.Trust || 0,
      NoFoundation: typeCounts.Foundation || 0,
    };
    const byId = new Map(seedNodes.map((n) => [n.ID, n]));
    const lines = seedNodes
      .filter((n) => n.ID !== "X0")
      .map((n) => {
        const parentId = n.parentIDs?.[0] || "X0";
        const parentType = String(byId.get(parentId)?.type || "");
        const showsPct = (parentType === "Non Quoted Operational Company" || parentType === "HoldingActive") &&
          n.percent !== undefined && n.percent !== null && n.percent !== "";
        const label = toMermaidHtmlLabel(labelForNodeHtml(n, seed.initialAHType));
        return showsPct ? `${parentId}-->|${n.percent}%|${n.ID}("${label}")` : `${parentId}-->${n.ID}("${label}")`;
      });
    setNodes(seedNodes);
    setFlowLines(lines);
    setInitialAHType(seed.initialAHType);
    setPaletteVisible(true);
  }, [customerName]);

  useEffect(() => {
    if (initialAHType || !initialStructure?.nodes?.length) return;
    applySeed(initialStructure);
  }, [initialStructure, initialAHType, applySeed]);

  // Document shareholders: once the client entity is placed and each company's type is answered, they are
  // placed under it (only once, on a structure still empty).
  const documentCompanies = useMemo(
    () => (Array.isArray(documentShareholders) ? documentShareholders : []).filter((p) => p?.kind === "company" && String(p?.name || "").trim()),
    [documentShareholders]
  );
  const [documentCompanyTypes, setDocumentCompanyTypes] = useState({});
  // A holding chosen for the company asked: active or passive next (in the same card).
  const [documentHoldingAsked, setDocumentHoldingAsked] = useState(false);
  const chooseDocumentCompanyType = (index, type) => {
    setDocumentHoldingAsked(false);
    setDocumentCompanyTypes((current) => ({ ...current, [index]: type }));
  };
  const documentSeedAppliedRef = useRef(false);
  const documentRootType = String(nodes[0]?.type || "");
  const documentSeedPending = !documentSeedAppliedRef.current && documentCompanies.length > 0 &&
    !!initialAHType && !["", "NA", "Holding"].includes(documentRootType) &&
    !initialStructure?.nodes?.length && nodes.length === 1;
  const pendingDocumentCompanyIndex = documentSeedPending
    ? documentCompanies.findIndex((_, index) => !documentCompanyTypes[index]) : -1;
  // While the types are asked, the diagram below already shows the direct shareholders: a company whose type is
  // not answered yet reads "?" (display only; its box is placed once every type is answered).
  useEffect(() => {
    if (!documentSeedPending) return;
    const showsPct = documentRootType === "Non Quoted Operational Company" || documentRootType === "HoldingActive";
    let companyIndex = 0;
    setFlowLines(documentShareholders.filter((p) => String(p?.name || "").trim()).map((p, index) => {
      const type = p.kind === "company" ? documentCompanyTypes[companyIndex++] || "?" : "Individual";
      const id = `D${index + 1}`;
      const label = toMermaidHtmlLabel(labelForNodeHtml({ ID: id, type, name: String(p.name).trim() }, documentRootType));
      return showsPct && Number.isFinite(p.percent) ? `X0-->|${p.percent}%|${id}("${label}")` : `X0-->${id}("${label}")`;
    }));
  }, [documentSeedPending, documentCompanyTypes]); // eslint-disable-line

  useEffect(() => {
    if (!documentSeedPending || pendingDocumentCompanyIndex !== -1) return;
    documentSeedAppliedRef.current = true;
    const root = nodes[0];
    let companyIndex = 0;
    const children = documentShareholders.filter((p) => String(p?.name || "").trim()).map((p, index) => {
      const company = p.kind === "company";
      const type = company ? documentCompanyTypes[companyIndex++] : "Individual";
      return {
        ID: `D${index + 1}`, parentIDs: ["X0"], type,
        name: String(p.name).trim(),
        percent: Number.isFinite(p.percent) ? p.percent : "",
        nationality: company ? "" : String(p.nationality || ""),
        // The builder keeps a company's country of incorporation as countryOfResidence.
        countryOfResidence: String((company ? p.country : p.countryOfResidence) || ""),
      };
    });
    const seed = builderSeedFromNodes([{ ...root, ID: "X0", type: documentRootType }, ...children]);
    if (seed) applySeed(seed);
  }, [documentSeedPending, pendingDocumentCompanyIndex]); // eslint-disable-line

  useEffect(() => {
    if (initialAHType || initialStructure?.nodes?.length) return;
    const requestedType = String(initialAccountHolderType || "").trim();
    if (!requestedType) return;
    startWithAH(requestedType, {
      trustNameKind: initialAccountHolderTrustNameKind,
    });
  }, [
    initialAHType,
    initialAccountHolderType,
    initialAccountHolderTrustNameKind,
    initialStructure,
    startWithAH,
  ]);

  const requestAddElement = useCallback(
    (newType, parentID) => {
      const parent = nodes.find((o) => o.ID === parentID);
      if (!parent) return;

      const parentType = parent.type;
      const parentLevel = parent.level;

      // ✅ terminal nodes
      if (TERMINAL_NODE_TYPES.has(parentType) || parentLevel >= 4) return;

      const { newId, typeNo, boolADE } = allocateNewNodeIds(
        newType,
        countersRef
      );

      // ✅ % required when parent is NQOC or HoldingActive
      const requiresPct =
        parentType === "Non Quoted Operational Company" ||
        parentType === "HoldingActive";

      pendingAddRef.current = {
        newType,
        parentID,
        parentType,
        requiresPct,
        draftId: newId,
        draftTypeNo: typeNo,
        draftBoolADE: boolADE,
      };

      setSubmitAttempted(false);
      setNameError("");

      setDetailsName(""); setDetailsNameParts({});
      setDetailsPct("");
      setPctError("");
      setDetailsNationality("");
      setDetailsDomicile("");
      setDetailsIncorporationCountry("");
      setCountryDetailsError("");
      setTrustRoles(emptyTrustRoles());
      setFoundationRoles(emptyFoundationRoles());
      setHoldingChoice("");
      setHoldingTouched(false);
      setDetailsOpen(true);
    },
    [nodes]
  );

  // ✅ Click on a node: open the same details modal, pre-filled, to edit it (not the account holder).
  const openEditNode = useCallback(
    (nodeId) => {
      const node = nodes.find((n) => n.ID === nodeId);
      if (!node || node.ID === "X0") return;
      const parentID = Array.isArray(node.parentIDs) ? node.parentIDs[0] : "";
      const parentType = nodes.find((n) => n.ID === parentID)?.type || "";
      const requiresPct =
        parentType === "Non Quoted Operational Company" || parentType === "HoldingActive";
      const isIndividual = node.type === "Individual";

      pendingAddRef.current = {
        mode: "edit",
        editId: node.ID,
        newType: node.type,
        parentID,
        parentType,
        requiresPct,
        draftId: node.ID,
        draftTypeNo: node.typeNo,
        draftBoolADE: node.boolADE,
      };

      setSubmitAttempted(false);
      setNameError("");
      setPctError("");
      setCountryDetailsError("");
      setDetailsName(String(node.name || ""));
      setDetailsNameParts(isIndividual ? nameFields(node) : {});
      setDetailsPct(requiresPct && node.percent != null ? String(node.percent) : "");
      setDetailsNationality(isIndividual ? String(node.nationality || "") : "");
      setDetailsDomicile(isIndividual ? String(node.countryOfResidence || "") : "");
      setDetailsIncorporationCountry(isIndividual ? "" : String(node.countryOfResidence || ""));
      setTrustRoles(node.type === "Trust" ? trustRolesFromNode(node.roles) : emptyTrustRoles());
      setFoundationRoles(node.type === "Foundation" ? foundationRolesFromNode(node.roles) : emptyFoundationRoles());
      setHoldingChoice(node.type === "HoldingActive" ? "active" : node.type === "HoldingPassive" ? "passive" : "");
      setHoldingTouched(false);
      setDetailsOpen(true);
    },
    [nodes]
  );

  // ✅ Remove a node and its whole branch (hovering the X fades that branch beforehand).
  const descendantsOf = useCallback(
    (nodeId) => nodes.filter((n) => Array.isArray(n.parentIDs) && n.parentIDs.includes(nodeId)),
    [nodes]
  );

  const removeNodeBranch = useCallback(
    (nodeId) => {
      if (!nodeId || nodeId === "X0") return;
      const removed = new Set([nodeId, ...descendantsOf(nodeId).map((n) => n.ID)]);
      setNodes((prev) => prev.filter((n) => !removed.has(n.ID)));
      setFlowLines((prev) => prev.filter((line) => !removed.has(flowLineTarget(line))));
      setUboResult(null);
    },
    [descendantsOf]
  );

  const requestDeleteNode = removeNodeBranch;

  const attachDropTargetsAndTooltips = useCallback(() => {
    const wrap = graphWrapRef.current;
    if (!wrap) return;

    const svg = wrap.querySelector("svg");
    if (!svg) return;

    const gNodes = Array.from(svg.querySelectorAll("g.node"));
    if (!gNodes.length) return;

    gNodes.forEach((g) => {
      const nodeId = extractNodeIdFromG(g);
      if (!nodeId) return;

      const nodeObj = nodes.find((n) => n.ID === nodeId);
      if (!nodeObj) return;

      const clone = g.cloneNode(true);
      clone.dataset.nodeid = nodeId;
      g.parentNode.replaceChild(clone, g);

      clone.style.pointerEvents = "all";

      clone.classList.remove("otRoleTooltipNode");
      clone.removeAttribute("data-pr-tooltip");

      if (nodeObj.type === "Trust") {
        clone.classList.add("otRoleTooltipNode");
        clone.setAttribute("data-pr-tooltip", trustTooltipText(nodeObj));
      } else if (nodeObj.type === "Foundation") {
        clone.classList.add("otRoleTooltipNode");
        clone.setAttribute("data-pr-tooltip", foundationTooltipText(nodeObj));
      }

      // Same rule as requestAddElement: nothing can be added under an individual, a quoted company,
      // a trust or a foundation, nor below level 4. Those nodes show no drop hint and refuse the drop.
      const acceptsChildren =
        !TERMINAL_NODE_TYPES.has(nodeObj.type) && !(Number(nodeObj.level) >= 4);

      clone.addEventListener("dragover", (e) => {
        if (!acceptsChildren) {
          if (e.dataTransfer) e.dataTransfer.dropEffect = "none";
          return;
        }
        e.preventDefault();
        clone.classList.add("drag-over-node");
      });

      clone.addEventListener("dragleave", () => {
        clone.classList.remove("drag-over-node");
      });

      clone.addEventListener("drop", (e) => {
        e.preventDefault();
        clone.classList.remove("drag-over-node");

        const from = e.dataTransfer.getData("text/plain");
        const toId = clone.dataset.nodeid;
        if (!from || !toId) return;

        const dragged = TYPES.find((t) => t.id === from);
        if (!dragged) return;

        requestAddElement(dragged.type, toId);
      });

      // ✅ Edit on click and a subtle remove "X" on hover (not on the account holder).
      if (nodeId !== "X0") {
        clone.classList.add("otEditableNode");
        clone.setAttribute("role", "button");
        clone.setAttribute("tabindex", "0");
        clone.setAttribute("aria-label", `Edit ${nodeObj.name || "element"}`);
        clone.addEventListener("click", () => openEditNode(nodeId));
        clone.addEventListener("keydown", (e) => {
          if (e.target !== clone) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openEditNode(nodeId);
          } else if (e.key === "Delete" || e.key === "Backspace") {
            e.preventDefault();
            requestDeleteNode(nodeId);
          }
        });

        const shape = clone.querySelector(".label-container") || clone.querySelector("rect, polygon, path");
        let box = null;
        try { box = (shape || clone).getBBox(); } catch { box = null; }
        if (box && box.width) {
          const SVG_NS = "http://www.w3.org/2000/svg";
          const del = document.createElementNS(SVG_NS, "g");
          del.setAttribute("class", "otNodeDelete");
          // Outside the top-right corner, with a gap. An invisible bridge over the gap keeps the node
          // hovered while the pointer travels to the X (the bridge itself neither previews nor removes).
          const cx = box.x + box.width + 9;
          const cy = box.y - 9;
          del.setAttribute("transform", `translate(${cx}, ${cy})`);
          const bridge = document.createElementNS(SVG_NS, "rect");
          bridge.setAttribute("class", "otNodeDeleteBridge");
          // Inline !important: Mermaid's class styles ("#<diagram> .<class> > *") would otherwise paint it.
          bridge.setAttribute("style", "fill: transparent !important; stroke: none !important; opacity: 0;");
          bridge.setAttribute("x", String(box.x + box.width - 6));
          bridge.setAttribute("y", String(cy - 9));
          bridge.setAttribute("width", String(cx + 9 - (box.x + box.width - 6)));
          bridge.setAttribute("height", String(box.y + 6 - (cy - 9)));
          bridge.addEventListener("click", (e) => e.stopPropagation());
          clone.appendChild(bridge);
          del.setAttribute("role", "button");
          del.setAttribute("tabindex", "0");
          del.setAttribute("aria-label", `Remove ${nodeObj.name || "element"}`);
          const title = document.createElementNS(SVG_NS, "title");
          title.textContent = "Remove";
          const circle = document.createElementNS(SVG_NS, "circle");
          circle.setAttribute("r", "9");
          const cross = document.createElementNS(SVG_NS, "path");
          cross.setAttribute("d", "M-3.2,-3.2L3.2,3.2M3.2,-3.2L-3.2,3.2");
          del.append(title, circle, cross);
          const remove = (e) => {
            e.preventDefault();
            e.stopPropagation();
            requestDeleteNode(nodeId);
          };
          // Hint: fade the node and everything below it (boxes, links, % labels) while the X is hovered.
          const branch = new Set([nodeId, ...nodes.filter((n) => (n.parentIDs || []).includes(nodeId)).map((n) => n.ID)]);
          const previewRemoval = (on) => {
            const svgEl = clone.ownerSVGElement;
            if (!svgEl) return;
            svgEl.querySelectorAll("g.node").forEach((g) => {
              g.classList.toggle("otDeletePreview", on && branch.has(g.dataset.nodeid));
            });
            // Mermaid tags links as L_<from>_<to>_<n> on the path and inside the label group.
            svgEl.querySelectorAll('path[id^="L_"], .edgeLabels [data-id^="L_"]').forEach((el) => {
              const target = String(el.dataset.id || el.id).split("_")[2];
              let item = el;
              while (item.parentElement && !item.parentElement.classList.contains("edgeLabels") && item.tagName !== "path") {
                item = item.parentElement;
              }
              item.classList.toggle("otDeletePreview", on && branch.has(target));
            });
          };
          del.addEventListener("mouseenter", () => previewRemoval(true));
          del.addEventListener("mouseleave", () => previewRemoval(false));
          del.addEventListener("focus", () => previewRemoval(true));
          del.addEventListener("blur", () => previewRemoval(false));
          del.addEventListener("mousedown", (e) => e.stopPropagation());
          del.addEventListener("click", remove);
          del.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") remove(e);
          });
          clone.appendChild(del);
        }
      }
    });

    tooltipRef.current?.updateTargetEvents?.();
  }, [nodes, requestAddElement, openEditNode, requestDeleteNode]);

  useEffect(() => {
    if (!graphWrapRef.current) return;

    if (!initialAHType || flowchartString === "graph TD;") {
      graphWrapRef.current.innerHTML = "";
      return;
    }

    let cancelled = false;

    const render = async () => {
      try {
        const { svg, bindFunctions } = await mermaid.render(
          "FlowchartUae",
          ownershipDiagram(flowchartString)
        );
        if (cancelled) return;

        graphWrapRef.current.innerHTML = svg;
        if (typeof bindFunctions === "function")
          bindFunctions(graphWrapRef.current);

        // Match edge label bg to .graphWrap background (#edeff1)
        const el = graphWrapRef.current;
        let builderBg = "";
        let bgAncestor = el.parentElement;
        while (bgAncestor && bgAncestor !== document.body) {
          const bg = getComputedStyle(bgAncestor).backgroundColor;
          if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") {
            builderBg = bg;
            break;
          }
          bgAncestor = bgAncestor.parentElement;
        }
        applyMermaidEdgeLabelBg(el, builderBg);

        attachDropTargetsAndTooltips();
      } catch (e) {
        console.error("Mermaid render error:", e);
        if (!cancelled) {
          graphWrapRef.current.innerHTML = `<pre style="color:#b00;white-space:pre-wrap;">Mermaid render error:\n${String(
            e?.message || e
          )}</pre>`;
        }
      }
    };

    render();
    return () => {
      cancelled = true;
    };
  }, [flowchartString, initialAHType, attachDropTargetsAndTooltips, ownershipDiagram]);

  // ✅ inline validation while typing
  const onPctChange = useCallback(
    (e) => {
      const raw = String(e.target.value || "");
      const pending = pendingAddRef.current;

      if (raw === "") {
        setDetailsPct("");
        setPctError("");
        return;
      }

      if (!/^\d*\.?\d*$/.test(raw)) return;

      setDetailsPct(raw);

      const n = Number(raw);
      if (!Number.isFinite(n)) return;

      if (n > 100) {
        setPctError("Invalid. Must be 0–100%.");
        return;
      }

      if (!pending?.requiresPct) {
        setPctError("");
        return;
      }

      const left = remainingPctForParent(pending.parentID, pending.editId);
      if (n > left + 1e-9) {
        setPctError(`Total exceeded. Max: ${formatPct(left)}%.`);
      } else {
        setPctError("");
      }
    },
    [remainingPctForParent]
  );

  // Trust beneficiaries helpers
  const updateTrustBeneficiary = useCallback((idx, field, v) => {
    setTrustRoles((prev) => {
      const list = [...(prev.beneficiaries || [])].map(normalizeTrustBeneficiary);
      list[idx] = {
        ...normalizeTrustBeneficiary(list[idx]),
        ...(typeof field === "object" ? field : { [field]: v }),
      };
      return { ...prev, beneficiaries: list };
    });
  }, []);
  const addTrustBeneficiaryRow = useCallback(() => {
    setTrustRoles((prev) => ({
      ...prev,
      beneficiaries: [
        ...(prev.beneficiaries || []),
        { name: "", nationality: "", domicile: "" },
      ],
    }));
  }, []);
  const removeTrustBeneficiaryRow = useCallback((idx) => {
    setTrustRoles((prev) => {
      const list = [...(prev.beneficiaries || [])].filter((_, i) => i !== idx);
      return {
        ...prev,
        beneficiaries: list.length
          ? list
          : [{ name: "", nationality: "", domicile: "" }],
      };
    });
  }, []);

  // Foundation beneficiaries helpers
  const updateFoundationBeneficiary = useCallback((idx, field, v) => {
    setFoundationRoles((prev) => {
      const list = [...(prev.beneficiaries || [])].map(
        normalizeFoundationBeneficiary
      );
      list[idx] = {
        ...normalizeFoundationBeneficiary(list[idx]),
        ...(typeof field === "object" ? field : { [field]: v }),
      };
      return { ...prev, beneficiaries: list };
    });
  }, []);
  const addFoundationBeneficiaryRow = useCallback(() => {
    setFoundationRoles((prev) => ({
      ...prev,
      beneficiaries: [
        ...(prev.beneficiaries || []),
        { name: "", nationality: "", domicile: "" },
      ],
    }));
  }, []);
  const removeFoundationBeneficiaryRow = useCallback((idx) => {
    setFoundationRoles((prev) => {
      const list = [...(prev.beneficiaries || [])].filter((_, i) => i !== idx);
      return {
        ...prev,
        beneficiaries: list.length
          ? list
          : [{ name: "", nationality: "", domicile: "" }],
      };
    });
  }, []);

  /* ---------------------------  Helpers ---------------------------------- */

  function detectComplexMultiLayer(nodes) {
    // Complex = there exists a path from X0 to any UBO that includes
    // more than one intermediate step (i.e., chain length >= 4 nodes:
    // X0 -> A -> B -> UBO or deeper)
    // Uses parentIDs chain which is [directParent, grandParent, ...]
    const byId = new Map((nodes || []).map((n) => [n.ID, n]));

    // find candidate endpoints: individuals and also role-based arrangements
    const endpoints = (nodes || []).filter((n) => {
      const t = String(n?.type || "");
      return t === "Individual" || t === "Trust" || t === "Foundation";
    });

    for (const n of endpoints) {
      // parentIDs contains ancestors excluding the node itself.
      // If direct parent is X0 => parentIDs length is 1.
      // If X0 -> A -> endpoint => parentIDs length is 2.
      // If X0 -> A -> B -> endpoint => parentIDs length is 3 (complex).
      const chainLen = Array.isArray(n.parentIDs) ? n.parentIDs.length : 0;

      // Ensure chain actually starts under X0 (last ancestor should be X0)
      const lastAncestor = chainLen > 0 ? n.parentIDs[chainLen - 1] : null;

      if (lastAncestor === "X0" && chainLen >= 3) return true;
    }

    return false;
  }

  /* ---------------------------  Helpers ---------------------------------- */

  function validateTrustRoles(r) {
    const errors = [];

    const settlorName = String(r.settlorName || "").trim();
    const settlorNationality = String(r.settlorNationality || "").trim();
    const settlorDomicile = String(r.settlorDomicile || "").trim();
    const trusteeName = String(r.trusteeName || "").trim();
    const trusteeKind = r.trusteeKind === "company" ? "company" : "individual";
    const trusteeCountry = String(r.trusteeCountry || "").trim();
    const trusteeNationality = String(r.trusteeNationality || "").trim();
    const trusteeDomicile = String(r.trusteeDomicile || "").trim();

    //  merge settlor+trustee into one message when both missing
    if (!settlorName && !trusteeName) {
      errors.push("Settlor and trustee names required.");
    } else {
      if (!settlorName) errors.push("Settlor name required.");
      if (!trusteeName) errors.push("Trustee name required.");
    }

    if (r.hasProtector) {
      const protectorName = String(r.protectorName || "").trim();
      if (!protectorName)
        errors.push("Protector name is required (or disable it).");
      if (OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED) {
        const protectorKind =
          r.protectorKind === "company" ? "company" : "individual";
        const protectorCountry = String(r.protectorCountry || "").trim();
        const protectorNationality = String(
          r.protectorNationality || ""
        ).trim();
        const protectorDomicile = String(r.protectorDomicile || "").trim();
        if (protectorKind === "company" && !protectorCountry) {
          errors.push("Protector country is required.");
        }
        if (
          protectorKind === "individual" &&
          (!protectorNationality || !protectorDomicile)
        ) {
          errors.push("Protector nationality and domicile required.");
        }
      }
    }

    if (!r.beneficiariesAreClass) {
      const list = (r.beneficiaries || []).map(normalizeTrustBeneficiary);
      const nonEmpty = list.filter((x) => String(x.name || "").trim());
      if (nonEmpty.length < 1) {
        errors.push(
          "At least one Beneficiary is required (or select class of beneficiaries)."
        );
      }
      if (OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED) {
        const missingCountryDetails = nonEmpty.some(
          (x) =>
            !String(x.nationality || "").trim() ||
            !String(x.domicile || "").trim()
        );
        if (missingCountryDetails) {
          errors.push("Beneficiary nationality and domicile required.");
        }
      }
    }

    if (
      OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED &&
      (!settlorNationality || !settlorDomicile)
    ) {
      errors.push("Settlor nationality and domicile required.");
    }

    if (OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED) {
      if (trusteeKind === "company" && !trusteeCountry) {
        errors.push("Trustee country is required.");
      }
      if (
        trusteeKind === "individual" &&
        (!trusteeNationality || !trusteeDomicile)
      ) {
        errors.push("Trustee nationality and domicile required.");
      }
    }

    if (errors.length) return { ok: false, errors };

    const cleanBeneficiaries = r.beneficiariesAreClass
      ? r.beneficiaries
      : (r.beneficiaries || [])
          .map(normalizeTrustBeneficiary)
          .map((x) => ({
            ...nameFields(x),
            name: String(x.name || "").trim(),
            nationality: String(x.nationality || ""),
            domicile: String(x.domicile || ""),
          }))
          .filter((x) => x.name);

    return {
      ok: true,
      clean: { ...r, trusteeKind, beneficiaries: cleanBeneficiaries },
    };
  }

  function validateFoundationRoles(r) {
    const errors = [];

    const founderName = String(r.founderName || "").trim();
    const founderNationality = String(r.founderNationality || "").trim();
    const founderDomicile = String(r.founderDomicile || "").trim();
    const councilName = String(r.councilName || "").trim();
    const councilKind = r.councilKind === "company" ? "company" : "individual";
    const councilCountry = String(r.councilCountry || "").trim();
    const councilNationality = String(r.councilNationality || "").trim();
    const councilDomicile = String(r.councilDomicile || "").trim();

    //  merge founder+council into one message when both missing
    if (!founderName && !councilName) {
      errors.push("Founder and council/board names required.");
    } else {
      if (!founderName) errors.push("Founder name required.");
      if (!councilName) errors.push("Council/board name required.");
    }

    if (r.hasGuardian) {
      const guardianName = String(r.guardianName || "").trim();
      const guardianKind =
        r.guardianKind === "company" ? "company" : "individual";
      const guardianCountry = String(r.guardianCountry || "").trim();
      const guardianNationality = String(r.guardianNationality || "").trim();
      const guardianDomicile = String(r.guardianDomicile || "").trim();
      if (!guardianName)
        errors.push("Guardian name is required (or disable it).");
      if (OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED) {
        if (guardianKind === "company" && !guardianCountry) {
          errors.push("Guardian country is required.");
        }
        if (
          guardianKind === "individual" &&
          (!guardianNationality || !guardianDomicile)
        ) {
          errors.push("Guardian nationality and domicile required.");
        }
      }
    }

    if (!r.beneficiariesAreClass) {
      const list = (r.beneficiaries || []).map(normalizeFoundationBeneficiary);
      const nonEmpty = list.filter((x) => String(x.name || "").trim());
      if (nonEmpty.length < 1) {
        errors.push(
          "At least one Beneficiary is required (or select class of beneficiaries)."
        );
      }
      if (OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED) {
        const missingCountryDetails = nonEmpty.some(
          (x) =>
            !String(x.nationality || "").trim() ||
            !String(x.domicile || "").trim()
        );
        if (missingCountryDetails) {
          errors.push("Beneficiary nationality and domicile required.");
        }
      }
    }

    if (
      OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED &&
      (!founderNationality || !founderDomicile)
    ) {
      errors.push("Founder nationality and domicile required.");
    }

    if (OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED) {
      if (councilKind === "company" && !councilCountry) {
        errors.push("Council/board country is required.");
      }
      if (
        councilKind === "individual" &&
        (!councilNationality || !councilDomicile)
      ) {
        errors.push("Council/board nationality and domicile required.");
      }
    }

    if (errors.length) return { ok: false, errors };

    const cleanBeneficiaries = r.beneficiariesAreClass
      ? r.beneficiaries
      : (r.beneficiaries || [])
          .map(normalizeFoundationBeneficiary)
          .map((x) => ({
            ...nameFields(x),
            name: String(x.name || "").trim(),
            nationality: String(x.nationality || ""),
            domicile: String(x.domicile || ""),
          }))
          .filter((x) => x.name);

    return {
      ok: true,
      clean: { ...r, councilKind, beneficiaries: cleanBeneficiaries },
    };
  }

  const closeDetails = useCallback(
    (reason = "cancel") => {
      const p = pendingAddRef.current;

      // ✅ If user cancels the Account Holder setup modal, abort the whole setup
      if (
        p?.isAccountHolder &&
        p?.draftId === "X0" &&
        (p?.newType === "Holding" ||
          p?.newType === "Trust" ||
          p?.newType === "Foundation")
      ) {
        if (reason === "overlay" || reason === "cancel") {
          resetAll();
          return;
        }
      }

      setHoldingTouched(false);
      setHoldingChoice("");
      setAccountHolderTrustNameKind("");

      setSubmitAttempted(false);
      setNameError("");

      setDetailsOpen(false);
      pendingAddRef.current = null;
      setPctError("");
      setDetailsNationality("");
      setDetailsDomicile("");
      setDetailsIncorporationCountry("");
      setCountryDetailsError("");
    },
    [resetAll]
  );

  // const closeDetails = useCallback(
  //   (reason = "cancel") => {
  //     const p = pendingAddRef.current;

  //     if (
  //       p?.isAccountHolder &&
  //       p?.draftId === "X0" &&
  //       p?.newType === "Holding"
  //     ) {
  //       if (reason === "overlay" || reason === "cancel") {
  //         resetAll();
  //         return;
  //       }
  //     }

  //     setHoldingTouched(false);
  //     setHoldingChoice("");

  //     setSubmitAttempted(false);
  //     setNameError("");

  //     setDetailsOpen(false);
  //     pendingAddRef.current = null;
  //     setPctError("");
  //   },
  //   [resetAll]
  // );

  const getSimpleDetailsMissingFields = useCallback(
    (pending, finalTypeArg) => {
      if (!pending) return [];
      const fields = [];
      const pendingTypeForValidation = pending.newType;
      const finalTypeForValidation = finalTypeArg || pendingTypeForValidation;
      const name = String(detailsName || "").trim();

      if (
        pending.isAccountHolder &&
        pending.draftId === "X0" &&
        pending.newType === "Holding"
      ) {
        if (holdingChoice !== "active" && holdingChoice !== "passive") {
          fields.push({
            kind: "holding",
            field: "holding",
            message: "Select whether the holding company is active or passive.",
          });
        }
        if (
          OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED &&
          !detailsIncorporationCountry
        ) {
          fields.push({
            kind: "country",
            field: "incorporation",
            message: "Country of incorporation is required.",
          });
        }
        return fields;
      }

      if (
        !(
          pending.isAccountHolder &&
          pending.draftId === "X0" &&
          (pending.newType === "Holding" ||
            pending.newType === "Trust" ||
            pending.newType === "Foundation")
        ) &&
        !name
      ) {
        fields.push({
          kind: "name",
          field: "name",
          message:
            pending.newType === "Trust"
              ? "Trust name is required."
              : pending.newType === "Foundation"
              ? "Foundation name is required."
              : pending.newType === "Individual"
              ? "Full name is required."
              : "Company name is required.",
        });
      }

      if (pendingTypeForValidation === "Holding") {
        if (holdingChoice !== "active" && holdingChoice !== "passive") {
          fields.push({
            kind: "holding",
            field: "holding",
            message: "Select whether the holding company is active or passive.",
          });
        }
      }

      if (OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED) {
        if (finalTypeForValidation === "Individual") {
          if (!detailsNationality) {
            fields.push({
              kind: "country",
              field: "nationality",
              message: "Nationality is required.",
            });
          }
          if (!detailsDomicile) {
            fields.push({
              kind: "country",
              field: "domicile",
              message: "Domicile is required.",
            });
          }
        } else if (
          entityTypeCapturesIncorporationCountry(finalTypeForValidation) ||
          pendingTypeForValidation === "Holding"
        ) {
          if (!detailsIncorporationCountry) {
            fields.push({
              kind: "country",
              field: "incorporation",
              message: "Country of incorporation is required.",
            });
          }
        }
      }

      if (pending.requiresPct && !String(detailsPct || "").trim()) {
        fields.push({
          kind: "pct",
          field: "pct",
          message: "Ownership percentage is required.",
        });
      }

      return fields;
    },
    [
      detailsName,
    detailsNameParts,
      detailsNationality,
      detailsDomicile,
      detailsIncorporationCountry,
      detailsPct,
      holdingChoice,
    ]
  );

  const showSingleMissingDetailsError = useCallback((fields) => {
    setNameError("");
    setCountryDetailsError("");
    setHoldingTouched(false);

    if (!Array.isArray(fields) || fields.length !== 1) return;

    const field = fields[0];
    if (field.kind === "name") {
      setNameError(field.message);
    } else if (field.kind === "country") {
      setCountryDetailsError(field.message);
    } else if (field.kind === "holding") {
      setHoldingTouched(true);
    } else if (field.kind === "pct") {
      setPctError(field.message);
    }
  }, []);

  const finalTypeForPendingDetails = useCallback((pending) => {
    if (!pending) return "";
    if (pending.newType !== "Holding") return pending.newType;
    if (holdingChoice === "active") return "HoldingActive";
    if (holdingChoice === "passive") return "HoldingPassive";
    return "Holding";
  }, [holdingChoice]);

  const confirmDetails = useCallback(() => {
    const pending = pendingAddRef.current;
    if (!pending) {
      setDetailsOpen(false);
      return false;
    }
    setCountryDetailsError("");

    // ✅ Account Holder = Holding: only pick active/passive, no name needed
    if (
      pending.isAccountHolder &&
      pending.draftId === "X0" &&
      pending.newType === "Holding"
    ) {
      const finalType =
        holdingChoice === "active" ? "HoldingActive" : "HoldingPassive";

      const missingFields = getSimpleDetailsMissingFields(pending, finalType);
      if (missingFields.length) {
        showSingleMissingDetailsError(missingFields);
        return false;
      }

      // Update X0 + initialAHType
      setNodes((prev) =>
        prev.map((n) =>
          n.ID === "X0"
            ? {
                ...n,
                type: finalType,
                countryOfResidence:
                  OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED
                    ? String(detailsIncorporationCountry || "")
                    : "",
              } // name already set from customerName
            : n
        )
      );

      setInitialAHType(finalType);

      setDetailsOpen(false);
      pendingAddRef.current = null;
      setDetailsIncorporationCountry("");
      setCountryDetailsError("");
      return true;
    }

    // ✅ Account Holder = Trust/Foundation: save roles on X0 (do NOT add a new node)
    if (
      pending.isAccountHolder &&
      pending.draftId === "X0" &&
      (pending.newType === "Trust" || pending.newType === "Foundation")
    ) {
      const isAccountHolderTrust = pending.newType === "Trust";
      const name =
        isAccountHolderTrust && accountHolderTrustNameKind === "trustee"
          ? String(detailsName || "").trim()
          : trimmedCustomerName;

      if (!name) {
        setNameError(
          pending.newType === "Trust"
            ? "Trust name is required."
            : "Foundation name is required."
        );
        return false;
      }
      setNameError("");

      let roles = null;

      if (pending.newType === "Trust") {
        if (
          accountHolderTrustNameKind !== "trust" &&
          accountHolderTrustNameKind !== "trustee"
        ) {
          return false;
        }

        const trustRolesForSave =
          accountHolderTrustNameKind === "trustee"
            ? {
                ...trustRoles,
                trusteeName: trimmedCustomerName,
              }
            : trustRoles;

        const v = validateTrustRoles(trustRolesForSave);
        if (!v.ok) return false;

        roles = {
          kind: "trust",
          settlor: {
            name: String(v.clean.settlorName).trim(),
            ...nameFields(v.clean.settlorNameParts),
            nationality: String(v.clean.settlorNationality || ""),
            domicile: String(v.clean.settlorDomicile || ""),
          },
          trustee: {
            kind: v.clean.trusteeKind,
            name: String(v.clean.trusteeName).trim(),
            ...nameFields(v.clean.trusteeNameParts),
            countryOfIncorporation: String(v.clean.trusteeCountry || ""),
            nationality: String(v.clean.trusteeNationality || ""),
            domicile: String(v.clean.trusteeDomicile || ""),
          },
          beneficiaries: v.clean.beneficiariesAreClass
            ? { kind: "class" }
            : {
                kind: "named",
                list: v.clean.beneficiaries.map((bn) => ({
                  ...nameFields(bn),
                  name: String(bn?.name || "").trim(),
                  nationality: String(bn?.nationality || ""),
                  domicile: String(bn?.domicile || ""),
                })),
              },
          protector: v.clean.hasProtector
            ? {
                kind:
                  v.clean.protectorKind === "company"
                    ? "company"
                    : "individual",
                name: String(v.clean.protectorName).trim(),
            ...nameFields(v.clean.protectorNameParts),
                countryOfIncorporation: String(
                  v.clean.protectorCountry || ""
                ),
                nationality: String(v.clean.protectorNationality || ""),
                domicile: String(v.clean.protectorDomicile || ""),
              }
            : null,
        };
      }

      if (pending.newType === "Foundation") {
        const v = validateFoundationRoles(foundationRoles);
        if (!v.ok) return false;

        roles = {
          kind: "foundation",
          founder: {
            name: String(v.clean.founderName).trim(),
            ...nameFields(v.clean.founderNameParts),
            nationality: String(v.clean.founderNationality || ""),
            domicile: String(v.clean.founderDomicile || ""),
          },
          council: {
            kind: v.clean.councilKind,
            name: String(v.clean.councilName).trim(),
            ...nameFields(v.clean.councilNameParts),
            countryOfIncorporation: String(v.clean.councilCountry || ""),
            nationality: String(v.clean.councilNationality || ""),
            domicile: String(v.clean.councilDomicile || ""),
          },
          beneficiaries: v.clean.beneficiariesAreClass
            ? { kind: "class" }
            : {
                kind: "named",
                list: v.clean.beneficiaries.map((bn) => ({
                  ...nameFields(bn),
                  name: String(bn?.name || "").trim(),
                  nationality: String(bn?.nationality || ""),
                  domicile: String(bn?.domicile || ""),
                })),
              },
          guardian: v.clean.hasGuardian
            ? {
                kind:
                  v.clean.guardianKind === "company" ? "company" : "individual",
                name: String(v.clean.guardianName).trim(),
            ...nameFields(v.clean.guardianNameParts),
                countryOfIncorporation: String(
                  v.clean.guardianCountry || ""
                ),
                nationality: String(v.clean.guardianNationality || ""),
                domicile: String(v.clean.guardianDomicile || ""),
              }
            : null,
        };
      }

      // ✅ persist onto X0
      setNodes((prev) =>
        prev.map((n) =>
          n.ID === "X0" ? { ...n, name, type: pending.newType, roles } : n
        )
      );

      setInitialAHType(pending.newType);

      setDetailsOpen(false);
      pendingAddRef.current = null;
      setDetailsNationality("");
      setDetailsDomicile("");
      setDetailsIncorporationCountry("");
      setCountryDetailsError("");
      return true;
    }

    const newType = pending.newType;
    const name = String(detailsName || "").trim();

    // Holding requires Active/Passive selection
    let finalType = newType;
    if (newType === "Holding") {
      finalType =
        holdingChoice === "active"
          ? "HoldingActive"
          : holdingChoice === "passive"
          ? "HoldingPassive"
          : "Holding";
    }

    const missingFields = getSimpleDetailsMissingFields(pending, finalType);
    if (missingFields.length) {
      showSingleMissingDetailsError(missingFields);
      return false;
    }
    setNameError("");
    setCountryDetailsError("");

    if (pctError) return false;

    let pct = 100;
    if (pending.requiresPct) {
      const raw = String(detailsPct || "").trim();
      if (raw === "") return false;

      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0 || n > 100) {
        setPctError("Percentage must be between 0 and 100.");
        return false;
      }

      const left = remainingPctForParent(pending.parentID, pending.editId);
      if (n > left + 1e-9) {
        setPctError(
          `Cannot exceed 100% at this level. Remaining available: ${formatPct(
            left
          )}%.`
        );
        return false;
      }

      setPctError("");
      pct = n;
    }

    let roles = null;

    if (newType === "Trust") {
      const v = validateTrustRoles(trustRoles);
      if (!v.ok) return false;

      roles = {
        kind: "trust",
        settlor: {
          name: String(v.clean.settlorName).trim(),
            ...nameFields(v.clean.settlorNameParts),
          nationality: String(v.clean.settlorNationality || ""),
          domicile: String(v.clean.settlorDomicile || ""),
        },
        trustee: {
          kind: v.clean.trusteeKind,
          name: String(v.clean.trusteeName).trim(),
            ...nameFields(v.clean.trusteeNameParts),
          countryOfIncorporation: String(v.clean.trusteeCountry || ""),
          nationality: String(v.clean.trusteeNationality || ""),
          domicile: String(v.clean.trusteeDomicile || ""),
        },
        beneficiaries: v.clean.beneficiariesAreClass
          ? { kind: "class" }
          : {
              kind: "named",
              list: v.clean.beneficiaries.map((bn) => ({
                ...nameFields(bn),
                  name: String(bn?.name || "").trim(),
                nationality: String(bn?.nationality || ""),
                domicile: String(bn?.domicile || ""),
              })),
            },
        protector: v.clean.hasProtector
          ? {
              kind:
                v.clean.protectorKind === "company" ? "company" : "individual",
              name: String(v.clean.protectorName).trim(),
            ...nameFields(v.clean.protectorNameParts),
              countryOfIncorporation: String(v.clean.protectorCountry || ""),
              nationality: String(v.clean.protectorNationality || ""),
              domicile: String(v.clean.protectorDomicile || ""),
            }
          : null,
      };
    }

    if (newType === "Foundation") {
      const v = validateFoundationRoles(foundationRoles);
      if (!v.ok) return false;

      roles = {
        kind: "foundation",
        founder: {
          name: String(v.clean.founderName).trim(),
            ...nameFields(v.clean.founderNameParts),
          nationality: String(v.clean.founderNationality || ""),
          domicile: String(v.clean.founderDomicile || ""),
        },
        council: {
          kind: v.clean.councilKind,
          name: String(v.clean.councilName).trim(),
            ...nameFields(v.clean.councilNameParts),
          countryOfIncorporation: String(v.clean.councilCountry || ""),
          nationality: String(v.clean.councilNationality || ""),
          domicile: String(v.clean.councilDomicile || ""),
        },
        beneficiaries: v.clean.beneficiariesAreClass
          ? { kind: "class" }
          : {
              kind: "named",
              list: v.clean.beneficiaries.map((bn) => ({
                ...nameFields(bn),
                  name: String(bn?.name || "").trim(),
                nationality: String(bn?.nationality || ""),
                domicile: String(bn?.domicile || ""),
              })),
            },
        guardian: v.clean.hasGuardian
          ? {
              kind:
                v.clean.guardianKind === "company" ? "company" : "individual",
              name: String(v.clean.guardianName).trim(),
            ...nameFields(v.clean.guardianNameParts),
              countryOfIncorporation: String(v.clean.guardianCountry || ""),
              nationality: String(v.clean.guardianNationality || ""),
              domicile: String(v.clean.guardianDomicile || ""),
            }
          : null,
      };
    }

    // ✅ Edit an existing node in place: same ID, parent and type; refresh its incoming flow line.
    if (pending.mode === "edit") {
      const editId = pending.editId;
      setDetailsOpen(false);
      setNodes((prev) =>
        prev.map((n) =>
          n.ID !== editId
            ? n
            : {
                ...n,
                name,
                ...(finalType === "Individual" ? nameFields(detailsNameParts) : {}),
                percent: pending.requiresPct ? pct : undefined,
                roles: finalType === "Trust" || finalType === "Foundation" ? roles || null : n.roles || null,
                nationality:
                  OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED && finalType === "Individual"
                    ? String(detailsNationality || "")
                    : n.nationality || "",
                countryOfResidence: !OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED
                  ? n.countryOfResidence || ""
                  : finalType === "Individual"
                  ? String(detailsDomicile || "")
                  : entityTypeCapturesIncorporationCountry(finalType)
                  ? String(detailsIncorporationCountry || "")
                  : n.countryOfResidence || "",
              }
        )
      );
      const editedLabel = toMermaidHtmlLabel(
        labelForNodeHtml({ ID: editId, type: finalType, name }, initialAHType)
      );
      setFlowLines((prev) =>
        prev.map((line) =>
          !isIncomingLineOf(line, pending.parentID, editId)
            ? line
            : pending.requiresPct
            ? `${pending.parentID}-->|${pct}%|${editId}("${editedLabel}")`
            : `${pending.parentID}-->${editId}("${editedLabel}")`
        )
      );
      setUboResult(null);
      pendingAddRef.current = null;
      setDetailsNationality("");
      setDetailsDomicile("");
      setDetailsIncorporationCountry("");
      setCountryDetailsError("");
      return true;
    }

    setDetailsOpen(false);

    // Add new node
    const newId = pending.draftId;

    setNodes((prev) => {
      const parent = prev.find((p) => p.ID === pending.parentID);
      if (!parent) return prev;

      const newParentIDs = [pending.parentID, ...(parent.parentIDs || [])];

      return [
        ...prev,
        {
          ID: newId,
          level: parent.level + 1,
          parentIDs: newParentIDs,
          type: finalType,
          typeNo: pending.draftTypeNo,
          // No % below a PIC, passive holding, trust or foundation: not applicable (never 100).
          percent: pending.requiresPct ? pct : undefined,
          boolADE: pending.draftBoolADE,
          name,
          ...(finalType === "Individual" ? nameFields(detailsNameParts) : {}),
          roles: roles || null,
          nationality:
            OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED &&
            finalType === "Individual"
              ? String(detailsNationality || "")
              : "",
          countryOfResidence:
            !OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED
              ? ""
              : finalType === "Individual"
              ? String(detailsDomicile || "")
              : entityTypeCapturesIncorporationCountry(finalType)
              ? String(detailsIncorporationCountry || "")
              : "",
        },
      ];
    });

    const nodeLabelHtml = labelForNodeHtml(
      { ID: newId, type: finalType, name },
      initialAHType
    );
    const safeLabel = toMermaidHtmlLabel(nodeLabelHtml);

    setFlowLines((prev) => {
      const edge = pending.requiresPct
        ? `${pending.parentID}-->|${pct}%|${newId}("${safeLabel}")`
        : `${pending.parentID}-->${newId}("${safeLabel}")`;
      return [...prev, edge];
    });

    pendingAddRef.current = null;
    setDetailsNationality("");
    setDetailsDomicile("");
    setDetailsIncorporationCountry("");
    setCountryDetailsError("");
    return true;
  }, [
    detailsName,
    detailsNameParts,
    detailsPct,
    pctError,
    detailsNationality,
    detailsDomicile,
    detailsIncorporationCountry,
    initialAHType,
    trustRoles,
    foundationRoles,
    holdingChoice,
    remainingPctForParent,
    getSimpleDetailsMissingFields,
    showSingleMissingDetailsError,
  ]);

  const submitDetailsFromEntryEnter = useCallback(
    (field) => {
      const pending = pendingAddRef.current;
      if (!pending || !field) return false;

      const finalType = finalTypeForPendingDetails(pending);
      const missingFields = getSimpleDetailsMissingFields(pending, finalType);

      if (missingFields.length === 0) {
        setSubmitAttempted(true);
        confirmDetails();
        return true;
      }

      return false;
    },
    [confirmDetails, finalTypeForPendingDetails, getSimpleDetailsMissingFields]
  );

  const handleSimpleDetailsCountryChange = useCallback(
    (value, setter) => {
      setter(value || "");
      if (countryDetailsError) setCountryDetailsError("");
    },
    [countryDetailsError]
  );

  const callUboApi = useCallback(async () => {
    const payload = { initialAHType, nodes };

	    setCallingApi(true);
	    try {
	      const data = await assessUbosFromTree(payload.initialAHType, payload.nodes, orgJurisdiction);

      setUboResult(data);
      onUboResult?.({ result: data, nodes, initialAHType });
    } catch (err) {
      console.error("UBO API call failed:", err);
    } finally {
      setCallingApi(false);
    }
  }, [initialAHType, nodes, onUboResult, orgJurisdiction]);

  const pendingType = pendingAddRef.current?.newType || "";
  const shouldShowIndividualCountryFields =
    OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED && pendingType === "Individual";
  const shouldShowInlineIncorporationCountry =
    OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED &&
    (pendingType === "PIC" ||
      pendingType === "Non Quoted Operational Company" ||
      pendingType === "Quoted Company" ||
      (pendingType === "Holding" && !pendingAddRef.current?.isAccountHolder));
  const shouldShowEntityCountryField =
    OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED &&
    !shouldShowInlineIncorporationCountry &&
    pendingType !== "Quoted Company" &&
    entityTypeCapturesIncorporationCountry(pendingType);
  const shouldShowCountryDetails =
    shouldShowIndividualCountryFields || shouldShowEntityCountryField;
  const isAccountHolderTrustQuestionStep =
    !!pendingAddRef.current?.isAccountHolder &&
    pendingType === "Trust" &&
    accountHolderTrustNameKind !== "trust" &&
    accountHolderTrustNameKind !== "trustee";

  const isSimpleDetailsComplete = useMemo(() => {
    const pending = pendingAddRef.current;
    if (!detailsOpen || !pending || isAccountHolderTrustQuestionStep) {
      return false;
    }

    const finalType = finalTypeForPendingDetails(pending);
    return getSimpleDetailsMissingFields(pending, finalType).length === 0;
  }, [
    detailsOpen,
    detailsName,
    detailsNameParts,
    detailsPct,
    detailsNationality,
    detailsDomicile,
    detailsIncorporationCountry,
    holdingChoice,
    isAccountHolderTrustQuestionStep,
    finalTypeForPendingDetails,
    getSimpleDetailsMissingFields,
  ]);

  const trustValidationMsg = useMemo(() => {
    if (pendingType !== "Trust") return "";
    if (
      pendingAddRef.current?.isAccountHolder &&
      accountHolderTrustNameKind !== "trust" &&
      accountHolderTrustNameKind !== "trustee"
    ) {
      return "Please select whether the current client name is the trust name or the trustee name.";
    }

    const effectiveTrustRoles =
      pendingAddRef.current?.isAccountHolder &&
      accountHolderTrustNameKind === "trustee"
        ? { ...trustRoles, trusteeName: trimmedCustomerName }
        : trustRoles;

    const v = validateTrustRoles(effectiveTrustRoles);
    return v.ok ? "" : v.errors.join(" ");
  }, [
    pendingType,
    trustRoles,
    accountHolderTrustNameKind,
    trimmedCustomerName,
  ]);

  const foundationValidationMsg = useMemo(() => {
    if (pendingType !== "Foundation") return "";
    const v = validateFoundationRoles(foundationRoles);
    return v.ok ? "" : v.errors.join(" ");
  }, [pendingType, foundationRoles]);

  /* ---------------------------  Helpers to fill kyc form ---------------------------------- */

  function makeRowId(prefix = "row") {
    return `${prefix}_${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  }

  function buildKycPayloadFromApiResult(
    apiResult,
    nodes,
    nodeById,
    effectivePctForNode,
    initialAHType
  ) {
    function round2(n) {
      const x = Number(n);
      if (!Number.isFinite(x)) return "";
      return String(Math.round(x * 100) / 100);
    }

    /**
     * ✅ NEW (core of this update):
     * We link each UBO row to the correct *direct shareholder row* using STABLE identifiers.
     *
     * Why this is recommended:
     * - Rebuilding links later in KycSectionOwnership is guessy/brittle (names, order, special cases).
     * - The tree already knows structure. So we carry a stable "node id" and "shareholder row id" link.
     *
     * What we store:
     * - In each shareholder row: treeNodeId (X1, X2, ...)
     * - In each UBO row: relatedShareholderNodeId + relatedShareholderRowId
     * - We keep legacy relatedShareholderIdx = "" (compat) because idx is not stable.
     */

    // ---- Resolve the "direct shareholder node id" for any node in the tree ----
    // Node.parentIDs is built as: [directParent, grandParent, ..., X0]
    // - If node is direct child of X0 => parentIDs = ["X0"] => direct shareholder is node itself.
    // - If node is deeper => direct shareholder is the ancestor *just before* X0 in parentIDs.
    function directShareholderNodeIdFor(nodeId) {
      if (!nodeId || nodeId === "X0") return null;

      const n = nodeById.get(nodeId);
      if (!n) return null;

      const chain = Array.isArray(n.parentIDs) ? n.parentIDs : [];
      const idxX0 = chain.indexOf("X0");
      if (idxX0 === -1) return null;

      // direct child of X0
      if (idxX0 === 0) return nodeId;

      // deeper: pick the ancestor right before X0
      return chain[idxX0 - 1] || null;
    }

    // ---- helper to push UBO rows (with stable shareholder link when possible) ----
    function pushUboRow(
      list,
      seen,
      {
        name,
        basisOfControl,
        pct,
        subjectNodeId,
        dsNodeIdToRowId,
        treeNodeId,
        meetsThreshold,
        nationality,
        countryOfResidence,
      }
    ) {
      const clean = String(name || "").trim();
      if (!clean) return;

      // One row per diagram box (or per party role of an arrangement), never merged by name.
      const key = uboRowKey({ treeNodeId, subjectNodeId, name: clean, basisOfControl });
      if (seen.has(key)) return;
      seen.add(key);

      // Determine the direct-shareholder node for THIS UBO (subjectNodeId)
      const relatedShareholderNodeId = subjectNodeId
        ? directShareholderNodeIdFor(subjectNodeId)
        : null;

      // Resolve direct-shareholder rowId (stable) from the map we build below
      const relatedShareholderRowId =
        relatedShareholderNodeId && dsNodeIdToRowId
          ? dsNodeIdToRowId.get(relatedShareholderNodeId) || ""
          : "";
      const subjectNode = subjectNodeId ? nodeById.get(subjectNodeId) : null;
      const subjectType = String(subjectNode?.type || "").trim();

      list.push({
        _rowId: makeRowId("ubo"),

        // ✅ Legacy field (kept for backward compatibility, but idx is NOT stable)
        relatedShareholderIdx: "",

        // ✅ NEW stable linking fields (preferred by KycSectionOwnership)
        relatedShareholderNodeId: relatedShareholderNodeId || "",
        relatedShareholderRowId: relatedShareholderRowId || "",

        // ✅ Individual's own tree node ID (enables explicit matching in review diagram)
        treeNodeId: treeNodeId || "",

        name: clean,
        // Given/family names entered in the builder for this person's box.
        ...(treeNodeId ? nameFields(nodeById.get(treeNodeId)) : {}),
        nationality:
          nationality !== undefined
            ? String(nationality || "")
            : subjectType === "Individual"
            ? String(subjectNode?.nationality || "")
            : "",
        countryOfResidence:
          countryOfResidence !== undefined
            ? String(countryOfResidence || "")
            : subjectType === "Individual"
            ? String(subjectNode?.countryOfResidence || "")
            : "",
        basisOfControl: basisOfControl || "Other",
        effectiveOwnershipPercent: pct,

        // ✅ true = meets threshold per API, false = below threshold, null = not yet assessed
        meetsThreshold: meetsThreshold ?? null,
      });
    }

    // ---------- Ownership meta (must match KycSectionOwnership flags) ----------
    const ahType = String(initialAHType || "").trim();

    // Matches your "PIC / passive holding" checkbox meaning:
    const isPctNotMeaningful = ahType === "PIC" || ahType === "HoldingPassive";

    const isQuotedCompany = ahType === "Quoted Company";
    const isTrustOrFoundation = ahType === "Trust" || ahType === "Foundation";

    const ownershipModel = isTrustOrFoundation
      ? "trust_foundation"
      : isPctNotMeaningful || isQuotedCompany
      ? "shareholding_no_pct"
      : "shareholding_pct";

    const shouldIncludePct = ownershipModel === "shareholding_pct";

    // ---------- Helper: direct % for direct shareholders ----------
    function directEdgePctFromX0(childId) {
      const child = nodeById.get(childId);
      if (!child) return "";

      // direct parent is first element in parentIDs chain
      const directParentId =
        Array.isArray(child.parentIDs) && child.parentIDs.length
          ? child.parentIDs[0]
          : null;

      // If this isn't a direct child of X0, fallback to effective %
      if (directParentId !== "X0") {
        const eff = effectivePctForNode(childId);
        return shouldIncludePct ? round2(eff) : "";
      }

      // if % is not meaningful, return empty
      if (!shouldIncludePct) return "";

      // When X0 type implies pct not meaningful, return empty
      const x0Type = String(initialAHType || "").trim();
      const pctNotMeaningfulForX0 =
        x0Type === "PIC" ||
        x0Type === "Trust" ||
        x0Type === "Foundation" ||
        x0Type === "HoldingPassive" ||
        x0Type === "Quoted Company";

      if (pctNotMeaningfulForX0) return "";

      // Use the direct percent stored on the child node
      const v = Number(child.percent);
      if (!Number.isFinite(v) || v <= 0 || v > 100) return "";
      return round2(v);
    }

    // ---------- Shareholders (DIRECT shareholders from TREE, not API) ----------
    const directChildIds = (nodes || [])
      .filter((n) => {
        if (!n || n.ID === "X0") return false;
        const directParent =
          Array.isArray(n.parentIDs) && n.parentIDs.length
            ? n.parentIDs[0]
            : null;
        return directParent === "X0";
      })
      .map((n) => n.ID);

	    const documentShareholderIds = apiResult
	      ? new Set([
	          ...(apiResult.documentShareholders || []),
	          ...(apiResult.directShareholders || []),
	        ])
	      : null;

	    // ✅ Create shareholder rows FIRST (each row gets _rowId)
	    const shareholders = directChildIds.map((id) => {
	      const n = nodeById.get(id);
	      const t = String(n?.type || "").trim();

      const isIndividual = t === "Individual";
      const isPic = t === "PIC";
      const isHoldingPassive = t === "HoldingPassive";
      const isHoldingActive = t === "HoldingActive";

      return {
        _rowId: makeRowId("sh"),

        // ✅ NEW: stable reference back to the tree
        // (so the form can always map "X3" -> this shareholder row)
        treeNodeId: id,

        name: String(n?.name || "").trim(),
        // Given/family names entered in the builder (separate-names mode).
        ...(isIndividual ? nameFields(n) : {}),

        // existing fields
        holderType: isIndividual ? "Individual" : "Entity",
        beneficialOwnerType: "Beneficial owner",
        nationality: isIndividual ? String(n?.nationality || "") : "",
        countryOfResidence: String(n?.countryOfResidence || ""),

        // existing pct behavior (kept)
        ownershipPercent: directEdgePctFromX0(id),

        // keep your extra context flags (kept)
        shareholderEntityType: isIndividual ? "Individual" : t || "Entity",
	        isPIC: isPic,
	        isHoldingPassive: isHoldingPassive,
	        isHoldingActive: isHoldingActive,
	        isPicOrPassiveHolding: isPic || isHoldingPassive,
	        requiresDocuments: documentShareholderIds
	          ? documentShareholderIds.has(id)
	          : null,
	      };
	    });

    // ✅ Build a stable map: directShareholderNodeId (X1/X2/...) -> shareholderRowId
    // This is what lets us set ubo.relatedShareholderRowId deterministically.
    const dsNodeIdToRowId = new Map();
    shareholders.forEach((sh) => {
      if (sh?.treeNodeId && sh?._rowId)
        dsNodeIdToRowId.set(sh.treeNodeId, sh._rowId);
    });

    function shareholderRowForNode(nodeId) {
      const directNodeId = directShareholderNodeIdFor(nodeId);
      const rowId = directNodeId ? dsNodeIdToRowId.get(directNodeId) : "";
      return shareholders.find((sh) => String(sh?._rowId || "") === String(rowId || ""));
    }

    function partiesFromRoles(type, roles) {
      const makeParty = (role, name, extra = {}) => ({
        _rowId: makeRowId("laP"),
        role,
        name: String(name || "").trim(),
        trusteeKind: "",
        nationality: String(extra.nationality || ""),
        domicile: String(extra.domicile || ""),
        autoCreated: true,
        ...extra,
      });

      if (type === "Trust" && roles?.kind === "trust") {
        const out = [
          makeParty("Settlor", roles?.settlor?.name, {
            ...nameFields(roles?.settlor),
            nationality: roles?.settlor?.nationality,
            domicile: roles?.settlor?.domicile,
          }),
          makeParty("Trustee", roles?.trustee?.name, {
            ...nameFields(roles?.trustee),
            trusteeKind: String(roles?.trustee?.kind || "").trim(),
            nationality: roles?.trustee?.nationality,
            domicile:
              roles?.trustee?.kind === "company"
                ? roles?.trustee?.countryOfIncorporation
                : roles?.trustee?.domicile,
          }),
        ];
        if (roles?.beneficiaries?.kind === "named") {
          (roles?.beneficiaries?.list || []).forEach((b) =>
            out.push(
              makeParty("Beneficiary", b?.name, {
                ...nameFields(b),
                nationality: b?.nationality,
                domicile: b?.domicile,
              })
            )
          );
        } else if (roles?.beneficiaries?.kind === "class") {
          out.push(makeParty("Beneficiary", "Class (unnamed)"));
        } else {
          out.push(makeParty("Beneficiary", ""));
        }
        if (roles?.protector?.name) {
          out.push(
            makeParty("Protector", roles.protector?.name, {
            ...nameFields(roles.protector),
              trusteeKind: String(roles?.protector?.kind || "").trim(),
              nationality: roles?.protector?.nationality,
              domicile:
                roles?.protector?.kind === "company"
                  ? roles?.protector?.countryOfIncorporation
                  : roles?.protector?.domicile,
            })
          );
        }
        return out;
      }

      if (type === "Foundation" && roles?.kind === "foundation") {
        const out = [
          makeParty("Founder", roles?.founder?.name, {
            ...nameFields(roles?.founder),
            nationality: roles?.founder?.nationality,
            domicile: roles?.founder?.domicile,
          }),
          makeParty("Council member", roles?.council?.name, {
            ...nameFields(roles?.council),
            trusteeKind: String(roles?.council?.kind || "").trim(),
            nationality: roles?.council?.nationality,
            domicile:
              roles?.council?.kind === "company"
                ? roles?.council?.countryOfIncorporation
                : roles?.council?.domicile,
          }),
        ];
        if (roles?.beneficiaries?.kind === "named") {
          (roles?.beneficiaries?.list || []).forEach((b) =>
            out.push(
              makeParty("Beneficiary", b?.name, {
                ...nameFields(b),
                nationality: b?.nationality,
                domicile: b?.domicile,
              })
            )
          );
        } else if (roles?.beneficiaries?.kind === "class") {
          out.push(makeParty("Beneficiary", "Class (unnamed)"));
        } else {
          out.push(makeParty("Beneficiary", ""));
        }
        if (roles?.guardian?.name) {
          out.push(
            makeParty("Guardian", roles.guardian?.name, {
            ...nameFields(roles.guardian),
              trusteeKind: String(roles?.guardian?.kind || "").trim(),
              nationality: roles?.guardian?.nationality,
              domicile:
                roles?.guardian?.kind === "company"
                  ? roles?.guardian?.countryOfIncorporation
                  : roles?.guardian?.domicile,
            })
          );
        }
        return out;
      }

      return [];
    }

    const arrangements = (nodes || [])
      .filter((n) => {
        if (!n || String(n?.ID || "") === "X0") return false;
        const t = String(n?.type || "").trim();
        return t === "Trust" || t === "Foundation";
      })
      .map((n) => {
        const t = String(n?.type || "").trim();
        const shRow = shareholderRowForNode(String(n?.ID || ""));
        return {
          _rowId: makeRowId("la"),
          arrangementType: t,
          name: String(n?.name || "").trim(),
          source: "shareholder",
          sourceRefRowId: String(shRow?._rowId || ""),
          relatedShareholderName:
            String(shRow?.name || "").trim() || String(customerName || "").trim(),
          autoCreated: true,
          parties: partiesFromRoles(t, n?.roles),
          _autoName: String(n?.name || "").trim(),
          _treeArrangementNodeId: String(n?.ID || ""),
        };
      });

    // ---------- UBOs (all Individual nodes; meetsThreshold flags which ones qualify) ----------
    const ubos = [];
    const seen = new Set();
    // null apiResult means "not yet assessed" — use null, not false
    const qualifyingUboIds = apiResult ? new Set(apiResult.ubos || []) : null;

    (nodes || []).forEach((node) => {
      const id = String(node?.ID || "");
      if (!id || id === "X0") return;
      if (String(node?.type || "").trim() !== "Individual") return;

      // The row keeps the person's % in their direct parent, only where that parent carries one
      // (shown and edited as "% in [company]"); never a cumulated % or a default 100%.
      const directParent = nodeById.get(String(node?.parentIDs?.[0] || ""));
      const parentPercent =
        directParent && (String(directParent.ID) !== "X0" || shouldIncludePct) && parentCarriesPercent(directParent) &&
        node?.percent !== "" && node?.percent != null ? round2(node.percent) : "";

      pushUboRow(ubos, seen, {
        name: String(node?.name || "").trim(),
        basisOfControl: "Indirect ownership",
        pct: parentPercent,
        subjectNodeId: id,
        dsNodeIdToRowId,
        treeNodeId: id,
        meetsThreshold: qualifyingUboIds ? qualifyingUboIds.has(id) : null,
      });
    });

    // ---------- Trust/Foundation roles injected as UBOs ----------
    // These always qualify regardless of % — meetsThreshold: true
    // When no API result, detect Trust/Foundation nodes directly from the tree
    const legalArrangementIds = apiResult
      ? (apiResult.legalArrangements || [])
      : (nodes || []).filter((n) => n?.type === "Trust" || n?.type === "Foundation").map((n) => String(n.ID));

    legalArrangementIds.forEach((arrId) => {
      const node = nodeById.get(arrId);
      const roles = node?.roles || null;
      if (!node || !roles) return;

      const pct = effectivePctForNode(arrId);

      const kindSuffix = (k) => {
        const raw = String(k || "").trim().toLowerCase();
        return raw === "company" || raw === "entity" || raw === "corporate"
          ? " (Corporate)"
          : " (Individual)";
      };

      // TRUST
      if (roles.kind === "trust") {
        pushUboRow(ubos, seen, {
          name: roles?.settlor?.name,
          basisOfControl: "Trust — Settlor",
          pct: shouldIncludePct ? round2(pct) : "",
          subjectNodeId: arrId,
          dsNodeIdToRowId,
          treeNodeId: "",
          meetsThreshold: true,
          nationality: roles?.settlor?.nationality,
          countryOfResidence: roles?.settlor?.domicile,
        });

        if (roles?.trustee?.name) {
          pushUboRow(ubos, seen, {
            name: roles.trustee.name,
            basisOfControl: `Trust — Trustee${kindSuffix(roles?.trustee?.kind)}`,
            pct: shouldIncludePct ? round2(pct) : "",
            subjectNodeId: arrId,
            dsNodeIdToRowId,
            treeNodeId: "",
            meetsThreshold: true,
            nationality:
              roles?.trustee?.kind === "individual"
                ? roles?.trustee?.nationality
                : "",
            countryOfResidence:
              roles?.trustee?.kind === "individual"
                ? roles?.trustee?.domicile
                : roles?.trustee?.countryOfIncorporation,
          });
        }

        if (roles?.beneficiaries?.kind === "named") {
          (roles?.beneficiaries?.list || []).forEach((b) => {
            pushUboRow(ubos, seen, {
              name: b?.name,
              basisOfControl: "Trust — Beneficiary",
              pct: shouldIncludePct ? round2(pct) : "",
              subjectNodeId: arrId,
              dsNodeIdToRowId,
              treeNodeId: "",
              meetsThreshold: true,
              nationality: b?.nationality,
              countryOfResidence: b?.domicile,
            });
          });
        }

        if (roles?.protector?.name) {
          pushUboRow(ubos, seen, {
            name: roles.protector.name,
            basisOfControl: `Trust — Protector${kindSuffix(roles?.protector?.kind)}`,
            pct: shouldIncludePct ? round2(pct) : "",
            subjectNodeId: arrId,
            dsNodeIdToRowId,
            treeNodeId: "",
            meetsThreshold: true,
            nationality:
              roles?.protector?.kind === "individual"
                ? roles?.protector?.nationality
                : "",
            countryOfResidence:
              roles?.protector?.kind === "individual"
                ? roles?.protector?.domicile
                : roles?.protector?.countryOfIncorporation,
          });
        }
      }

      // FOUNDATION
      if (roles.kind === "foundation") {
        pushUboRow(ubos, seen, {
          name: roles?.founder?.name,
          basisOfControl: "Foundation — Founder",
          pct: shouldIncludePct ? round2(pct) : "",
          subjectNodeId: arrId,
          dsNodeIdToRowId,
          treeNodeId: "",
          meetsThreshold: true,
          nationality: roles?.founder?.nationality,
          countryOfResidence: roles?.founder?.domicile,
        });

        if (roles?.council?.name) {
          pushUboRow(ubos, seen, {
            name: roles.council.name,
            basisOfControl: `Foundation — Council/Board${kindSuffix(roles?.council?.kind)}`,
            pct: shouldIncludePct ? round2(pct) : "",
            subjectNodeId: arrId,
            dsNodeIdToRowId,
            treeNodeId: "",
            meetsThreshold: true,
            nationality:
              roles?.council?.kind === "individual"
                ? roles?.council?.nationality
                : "",
            countryOfResidence:
              roles?.council?.kind === "individual"
                ? roles?.council?.domicile
                : roles?.council?.countryOfIncorporation,
          });
        }

        if (roles?.beneficiaries?.kind === "named") {
          (roles?.beneficiaries?.list || []).forEach((b) => {
            pushUboRow(ubos, seen, {
              name: b?.name,
              basisOfControl: "Foundation — Beneficiary",
              pct: shouldIncludePct ? round2(pct) : "",
              subjectNodeId: arrId,
              dsNodeIdToRowId,
              treeNodeId: "",
              meetsThreshold: true,
              nationality: b?.nationality,
              countryOfResidence: b?.domicile,
            });
          });
        }

        if (roles?.guardian?.name) {
          pushUboRow(ubos, seen, {
            name: roles.guardian.name,
            basisOfControl: `Foundation — Guardian${kindSuffix(roles?.guardian?.kind)}`,
            pct: shouldIncludePct ? round2(pct) : "",
            subjectNodeId: arrId,
            dsNodeIdToRowId,
            treeNodeId: "",
            meetsThreshold: true,
            nationality:
              roles?.guardian?.kind === "individual"
                ? roles?.guardian?.nationality
                : "",
            countryOfResidence:
              roles?.guardian?.kind === "individual"
                ? roles?.guardian?.domicile
                : roles?.guardian?.countryOfIncorporation,
          });
        }
      }
    });

    const complexStructure = detectComplexMultiLayer(nodes);

    return {
      shareholders,
      ubos,
      arrangements,
      raw: apiResult,

      ownershipModel,

      // The assistant currently models ownership structure, not separate
      // "other form of control" arrangements like shareholder agreements.
      controlOtherThanShareholding: false,

      controlFlags: {
        pctNotMeaningful: !!isPctNotMeaningful,
        quotedCompany: !!isQuotedCompany,
        trustFoundation: !!isTrustOrFoundation,
      },

      complexStructure,
    };
  }

  // Dedicated function for “Update KYC” => calls same API, opens the preview modal.
  const runUpdateKycPreview = useCallback(async () => {
    setKycPreviewErr("");

    // 1) BLOCK submit if X0 has NO children (unless exempt AH types)
    const exemptAhTypes = new Set(["Trust", "Foundation", "Quoted Company"]);
    const ahType = String(initialAHType || "").trim();
    const x0ChildrenCount = (directChildrenOf("X0") || []).length;

    if (x0ChildrenCount === 0 && !exemptAhTypes.has(ahType)) {
      setSubmitAlertMode("noChildren");
      setSubmitAlertLeaves([]); // nothing to list
      setSubmitAlertOpen(true);
      return;
    }

    // 2) BLOCK submit if some branches end with an invalid leaf
    const invalidLeaves = findInvalidTerminalLeaves(
      nodes,
      initialAHType,
      customerName
    );
    if (invalidLeaves.length) {
      setSubmitAlertMode("invalidLeaves");
      setSubmitAlertLeaves(invalidLeaves);
      setSubmitAlertOpen(true);
      return;
    }

    setKycPreviewLoading(true);

    // Client mode: skip API call — threshold assessment happens at Approve time
    if (clientMode) {
      try {
        const built = buildKycPayloadFromApiResult(
          null,
          nodes,
          nodeById,
          effectivePctForNode,
          initialAHType
        );
        built.assistantTree = {
          mermaid: flowchartString,
          initialAHType,
          source: "visualBuilder",
          nodes,
          flowLines,
        };
        built.accountHolderTrustResolution =
          initialAHType === "Trust" &&
          (accountHolderTrustNameKind === "trust" ||
            accountHolderTrustNameKind === "trustee")
            ? { kind: accountHolderTrustNameKind, sourceName: trimmedCustomerName }
            : null;
        if (SHOW_KYC_PREVIEW_CONFIRMATION) {
          setKycPreviewPayload(built);
          setKycPreviewOpen(true);
        } else {
          if (onApplyKycUpdate?.(built) !== false) onClose?.();
        }
      } catch (e) {
        setKycPreviewErr(String(e?.message || e));
      } finally {
        setKycPreviewLoading(false);
      }
      return;
    }

	    try {
	      const payload = { initialAHType, nodes };

	      const data = await assessUbosFromTree(payload.initialAHType, payload.nodes, orgJurisdiction);

      setKycPreviewResult(data);
      const built = buildKycPayloadFromApiResult(
        data,
        nodes,
        nodeById,
        effectivePctForNode,
        initialAHType
      );

      // ✅ snapshot for read-only review card in KycSectionOwnership
      built.assistantTree = {
        mermaid: flowchartString,
        initialAHType,
        source: "visualBuilder",
        nodes,
        flowLines,
      };
      built.accountHolderTrustResolution =
        initialAHType === "Trust" &&
        (accountHolderTrustNameKind === "trust" ||
          accountHolderTrustNameKind === "trustee")
          ? {
              kind: accountHolderTrustNameKind,
              sourceName: trimmedCustomerName,
            }
          : null;

      if (SHOW_KYC_PREVIEW_CONFIRMATION) {
        setKycPreviewPayload(built);
        setKycPreviewOpen(true);
      } else {
        if (onApplyKycUpdate?.(built) !== false) onClose?.();
      }
    } catch (e) {
      setKycPreviewErr(String(e?.message || e));
    } finally {
      setKycPreviewLoading(false);
    }
  }, [
    clientMode,
    orgJurisdiction,
    initialAHType,
    nodes,
    nodeById,
    effectivePctForNode,
    directChildrenOf,
    flowchartString,
    flowLines,
    accountHolderTrustNameKind,
    trimmedCustomerName,
    onApplyKycUpdate,
    onClose,
  ]);

  const confirmKycPreviewUpdate = useCallback(() => {
    if (!kycPreviewPayload) return;

    const invalidLeaves = findInvalidTerminalLeaves(
      nodes,
      initialAHType,
      customerName
    );
    if (invalidLeaves.length) {
      setSubmitAlertLeaves(invalidLeaves);
      setSubmitAlertOpen(true);
      return;
    }

    onApplyKycUpdate?.(kycPreviewPayload);
    setKycPreviewOpen(false);
    onClose?.();
  }, [
    kycPreviewPayload,
    nodes,
    initialAHType,
    customerName,
    onApplyKycUpdate,
    onClose,
  ]);

  useEffect(() => {
    function handleGlobalEnterKey(e) {
      if (e.key !== "Enter") return;
      if (detailsOpen) return;
      if (isOwnershipAssistantFormEntryTarget(e.target)) return;

      e.preventDefault();
      e.stopPropagation();

      if (submitAlertOpen) {
        setSubmitAlertOpen(false);
        return;
      }

      if (kycPreviewOpen) {
        confirmKycPreviewUpdate();
        return;
      }

      if (callingApi || kycPreviewLoading || !initialAHType) return;
      runUpdateKycPreview();
    }

    document.addEventListener("keydown", handleGlobalEnterKey);
    return () => {
      document.removeEventListener("keydown", handleGlobalEnterKey);
    };
  }, [
    detailsOpen,
    kycPreviewOpen,
    submitAlertOpen,
    callingApi,
    kycPreviewLoading,
    initialAHType,
    confirmKycPreviewUpdate,
    runUpdateKycPreview,
  ]);

  // Derived UI text helper for alert if no UBOs for one end entity
  const endCount = useMemo(
    () => (submitAlertLeaves || []).length,
    [submitAlertLeaves]
  );

  /* -------------------------------------------------------------------------- */
  /* ------------------------------   RETURN ---------------------------------- */
  /* -------------------------------------------------------------------------- */

  // Top card: the company type question (document shareholders) or the builder header.
  function renderTopCard() {
    return (
      <AnimatePresence mode="wait" initial={false}>
        {documentSeedPending ? (
          <motion.div key="document-company-question" exit={{ opacity: 0 }} transition={{ duration: 0.25, ease: "easeInOut" }}>
            {renderDocumentCompanyQuestion()}
          </motion.div>
        ) : (
          <motion.div key="builder-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35, ease: "easeInOut" }}>
          <div className="cdbTreeHeader">
            {!initialAHType ? (
              <div className="cdbTreeHeaderTitle">Select Account Holder Type</div>
            ) : (
              <div className="cdbTreeHeaderTopRow">
                <div className="cdbTreeHeaderActions cdbTreeHeaderActionsLeft">
                  <button
                    type="button"
                    className="otResetIconBtn"
                    onClick={resetAll}
                    disabled={callingApi || kycPreviewLoading}
                    title="Reset"
                    aria-label="Reset"
                  >
                    <i
                      className="bi bi-arrow-counterclockwise"
                      aria-hidden="true"
                    />
                  </button>
                </div>

                <div className="cdbTreeHeaderHint cdbTreeHeaderHintCenter">
                  Drag the relevant element into the ownership structure below
                </div>

                <div className="cdbTreeHeaderActions cdbTreeHeaderActionsRight">
                  <button
                    className="toolbarBtn toolbarBtnFinish"
                    onClick={runUpdateKycPreview}
                    disabled={kycPreviewLoading || callingApi}
                    type="button"
                  >
                    {!kycPreviewLoading && (
                      <i className="bi bi-check2-circle me-2" aria-hidden="true" />
                    )}
                    <span className="cdbTreeSubmitText">
                      {kycPreviewLoading ? "Preparing..." : "Finish"}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {paletteVisible && (
              <div className="cdbTreePalette">
                {TYPES.map((t) => (
                  <span
                    key={t.id}
                    className="paletteBox"
                    draggable
                    onDragStart={(e) => onDragStart(e, t.id)}
                    title="Drag onto a node"
                  >
                    <i className="bi bi-grip-vertical" style={{ opacity: 0.7, marginRight: '5px' }} aria-hidden="true" />
                    {t.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  function renderDocumentCompanyQuestion() {
    const index = pendingDocumentCompanyIndex === -1 ? documentCompanies.length - 1 : pendingDocumentCompanyIndex;
    const company = String(documentCompanies[index]?.name || "").trim();
    return (
      <div className="otDocCompanyQuestion" role="group" aria-label="Company shareholder type">
        <p className="otDocCompanyIntro">
          <i className="bi bi-info-circle" aria-hidden="true" />
          {companyShareholdersNotice(documentCompanies.map((p) => String(p.name).trim()), String(customerName || "").trim(),
            documentShareholders.filter((p) => String(p?.name || "").trim()).length)}
        </p>
        <div className="otDocCompanyAsk">
          {documentHoldingAsked ? <>Which kind of holding is <strong>{company}</strong>?</> : <>What type of company is <strong>{company}</strong>?</>}
          {documentCompanies.length > 1 ? <span className="otDocCompanyStep">{index + 1} of {documentCompanies.length}</span> : null}
        </div>
        <AnimatePresence mode="wait" initial={false}>
          {documentHoldingAsked ? (
            <motion.div key="holding" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="kycEntityTypeList is-compact" role="radiogroup" aria-label="Holding type">
                {HOLDING_KINDS.map((option) => (
                  <button key={option.value} type="button" role="radio" aria-checked="false" className="kycEntityTypeOption"
                    onClick={() => chooseDocumentCompanyType(index, DOCUMENT_COMPANY_BUILDER_TYPES[option.value === "active" ? "holdingActive" : "holdingPassive"])}>
                    <span><strong>{option.label}</strong><small>{option.hint}</small></span>
                  </button>
                ))}
              </div>
              <button type="button" className="otDocCompanyBack" onClick={() => setDocumentHoldingAsked(false)}>
                <i className="bi bi-arrow-left" aria-hidden="true" /> Other types
              </button>
            </motion.div>
          ) : (
            <motion.div key="types" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
              className="kycEntityTypeOptions is-cards otDocCompanyCards">
              <div className="kycEntityTypeList" role="radiogroup" aria-label="Company type">
                {DOCUMENT_COMPANY_CHOICES.map((option) => (
                  <button key={option.value} type="button" role="radio" aria-checked="false" className="kycEntityTypeOption"
                    aria-label={ownershipLabel(option.title)}
                    onClick={() => (option.value === "holding"
                      ? setDocumentHoldingAsked(true)
                      : chooseDocumentCompanyType(index, DOCUMENT_COMPANY_BUILDER_TYPES[option.value]))}>
                    <span className="kycEntityTypeIcon" aria-hidden="true"><img src={option.iconSrc} alt="" /></span>
                    <span className="kycEntityTypeText">
                      <strong>{ownershipLabel(option.title)}</strong>
                      <small>{option.hint}</small>
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="cdbTreeRoot">
      <Tooltip
        ref={tooltipRef}
        target=".otRoleTooltipNode"
        mouseTrack
        mouseTrackLeft={12}
        mouseTrackTop={18}
        className="otRoleTooltip"
        baseZIndex={500000}
        appendTo="self"
      />

      {/* Company shareholders read in the documents: their type is asked in place of the header (drag hint and
          palette); then the question fades out, and the diagram moves up while the header fades in. */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: BUILDER_TOP_FADE_S, ease: "easeOut" }}>
        {documentCompanies.length > 0 ? <MeasuredHeight>{renderTopCard()}</MeasuredHeight> : renderTopCard()}
      </motion.div>

      {/* Opening: the top card fades in first, then the diagram below. */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: BUILDER_TOP_FADE_S, ease: "easeOut" }}>
      <div className="cdbTreeMain">
        {!initialAHType ? (
          <div className="graphWrap graphWrapStartPadding">
            <div className="cdbTreeAhButtonsWrap">
              <button
                className="ahBtn"
                onClick={() => startWithAH("Non Quoted Operational Company")}
              >
                Non Quoted Operational Company
              </button>
              <button className="ahBtn" onClick={() => startWithAH("Holding")}>
                Holding
              </button>
              <button className="ahBtn" onClick={() => startWithAH("PIC")}>
                {ownershipLabel("Domiciliary company (PIC)")}
              </button>
              <button className="ahBtn" onClick={() => startWithAH("Trust")}>
                Trust
              </button>
              <button
                className="ahBtn"
                onClick={() => startWithAH("Foundation")}
              >
                Foundation
              </button>

              <button
                className="ahBtn"
                onClick={() => startWithAH("Quoted Company")}
              >
                Quoted Company
              </button>
            </div>
          </div>
        ) : (
          <div className="graphWrap">
            <div ref={graphWrapRef} />
          </div>
        )}
      </div>
      </motion.div>

      {/* ✅ UBO Result (preview) */}
      {uboResult && (
        <div style={{ width: "95%", margin: "12px auto 0" }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            UBO Result (preview)
          </div>

          <div style={{ marginBottom: 10, lineHeight: 1.5 }}>
            {!!uboResult?.directShareholders?.length && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontWeight: 600 }}>Direct shareholders</div>
                <ul style={{ margin: "4px 0 0 18px" }}>
                  {uboResult.directShareholders.map((id) => (
                    <li key={`ds-${id}`}>
                      {idToLabel(id)}{" "}
                      <span style={{ opacity: 0.8 }}>
                        ({formatPct(effectivePctForNode(id))}%)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!!uboResult?.ubos?.length && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontWeight: 600 }}>UBOs (Individuals)</div>
                <ul style={{ margin: "4px 0 0 18px" }}>
                  {uboResult.ubos.map((id) => (
                    <li key={`ubo-${id}`}>
                      {idToLabel(id)}{" "}
                      <span style={{ opacity: 0.8 }}>
                        ({formatPct(effectivePctForNode(id))}%)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!!uboResult?.legalArrangements?.length && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontWeight: 600 }}>
                  UBO arrangements (Trust/Foundation)
                </div>

                <div style={{ marginTop: 6 }}>
                  {uboResult.legalArrangements.map((id) => {
                    const node = nodeById.get(id);
                    const roles = node?.roles || null;

                    return (
                      <div
                        key={`arr-${id}`}
                        style={{
                          background: "#fff",
                          borderRadius: 12,
                          padding: "10px 12px",
                          marginBottom: 10,
                          border: "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>
                          {idToLabel(id)}{" "}
                          <span style={{ opacity: 0.8 }}>
                            ({formatPct(effectivePctForNode(id))}%)
                          </span>
                        </div>

                        {node?.type === "Trust" &&
                          renderTrustRolesInline(roles)}
                        {node?.type === "Foundation" &&
                          renderFoundationRolesInline(roles)}

                        {!roles && (
                          <div style={{ marginTop: 6, color: "#b00020" }}>
                            Roles not captured.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <pre
            style={{
              whiteSpace: "pre-wrap",
              background: "#fff",
              padding: 12,
              borderRadius: 12,
            }}
          >
            {JSON.stringify(uboResult, null, 2)}
          </pre>
        </div>
      )}

      <AnimatePresence>
        {detailsOpen && (
          <motion.div
            className="otModalOverlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            onMouseDown={() => closeDetails("overlay")}
          >
            <motion.div
              className="otModalCard"
              initial={{ opacity: 0, y: 0, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 14, scale: 0.985 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (isOwnershipAssistantFormEntryTarget(e.target)) {
                    const field = ownershipAssistantEntryField(e.target);
                    if (submitDetailsFromEntryEnter(field)) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                    return;
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  setSubmitAttempted(true);
                  confirmDetails();
                }
              }}
              style={{ willChange: "transform, opacity" }}
            >
              <div className="otModalHeader">
                <div>
                  <div className="otModalTitle">
                    {pendingAddRef.current?.mode === "edit" ? "Edit · " : ""}
                    {ownershipLabel(modalTitleForPendingType(pendingType))}
                  </div>

                  <div className="otModalSubtitle">
                    {modalSubtitleForPendingType(pendingType)}
                  </div>
                </div>
              </div>

              {/* --- Name + optional % --- */}
              {!(
                (
                  pendingAddRef.current?.isAccountHolder &&
                  (pendingType === "Holding" ||
                    pendingType === "Trust" ||
                    pendingType === "Foundation")
                )
                // pendingType === "Holding"
              ) && (
                <div className="otModalSection">
                  <div className="otModalSectionTitle">
                    <span className="otPill otPillAccent">
                      {pendingType === "Trust"
                        ? "Trust Name"
                        : pendingType === "Foundation"
                        ? "Foundation Name"
                        : pendingType === "Individual"
                        ? "Full Name"
                        : "Company Name"}
                    </span>
                  </div>

                  <div
                    className={
                      shouldShowInlineIncorporationCountry &&
                      pendingAddRef.current?.requiresPct
                        ? "otGrid3Pic"
                        : shouldShowInlineIncorporationCountry
                        ? "otGrid2PicNoPct"
                        : pendingAddRef.current?.requiresPct
                        ? "otGrid2c"
                        : "otGrid2a"
                    }
                    style={{ alignItems: "start" }}
                  >
                    <div data-ot-entry-field="name">
                      {(pendingAddRef.current?.requiresPct ||
                        shouldShowInlineIncorporationCountry) && (
                        <div className="otFieldLabel otLabelSpacer">.</div>
                      )}

                      {separateNames && pendingType === "Individual" ? (
                        <IndividualNamePreview
                          inputClass="otInput"
                          displayName={detailsName}
                          value={detailsNameParts} onChange={(patch) => { setDetailsNameParts(nameFields(patch)); setDetailsName(patch.name); setNameError(""); }}
                          framed
                        />
                      ) : (
                      <input
                        className="otInput"
                        value={detailsName}
                        onChange={(e) => {
                          setDetailsName(pendingType === "Individual" ? capitalizeNameInitials(e.target.value) : e.target.value);
                          if (nameError) setNameError("");
                        }}
                        autoFocus
                        placeholder={
                          pendingType === "Trust"
                            ? "Trust name (e.g. The Smith Family Trust)"
                            : pendingType === "Foundation"
                            ? "Foundation name (e.g. ABC Foundation)"
                            : pendingType === "Individual"
                            ? "Full name (e.g. John Smith)"
                            : pendingType === "Holding"
                            ? "Company name (e.g. ACME Holding Ltd)"
                            : "Company name (e.g. ACME Ltd)"
                        }
                      />
                      )}
                      {submitAttempted && !!nameError && (
                        <div
                          style={{
                            marginTop: 10,
                            color: "#b00020",
                            fontSize: 13,
                            fontWeight: 500,
                            fontFamily: "Roboto",
                          }}
                        >
                          {nameError}
                        </div>
                      )}
                    </div>

                    {shouldShowInlineIncorporationCountry && (
                      <div
                        className="otPicCompactField otPicCountryCompactField"
                        data-ot-entry-field="incorporation"
                      >
                        <div className="otFieldLabel otPicCountryLabel">
                          Country of incorporation
                        </div>
                        <CountrySelect
                          value={detailsIncorporationCountry}
                          onChange={(value) => {
                            handleSimpleDetailsCountryChange(
                              value,
                              setDetailsIncorporationCountry
                            );
                          }}
                          className="otCountrySelect"
                          optionTextClassName="otCountryOptionText"
                          singleValueMode={
                            pendingAddRef.current?.requiresPct
                              ? "iso3"
                              : "label"
                          }
                          menuMinWidth={220}
                          menuPortalZIndex={100010}
                          optionFontSize={13}
                          placeholder="Select country"
                        />
                      </div>
                    )}

                    {pendingAddRef.current?.requiresPct && (
                      <div
                        className="otPicCompactField otOwnershipCompactField"
                        data-ot-entry-field="pct"
                      >
                        <div className="otFieldLabel">Ownership %</div>
                        <input
                          className="otInput"
                          value={detailsPct}
                          onChange={onPctChange}
                          inputMode="decimal"
                          type="text"
                          placeholder="0-100"
                        />

                        <div
                          className="otHint"
                          style={{
                            marginTop: 6,
                            color: pctError ? "#b00020" : undefined,
                            fontWeight: pctError ? 600 : undefined,
                          }}
                        >
                          {pctError
                            ? pctError
                            : `Remaining: ${formatPct(
                                leftForPendingParent ?? 100
                              )}%`}
                        </div>
                      </div>
                    )}
                  </div>
                  {shouldShowInlineIncorporationCountry && !!countryDetailsError && (
                    <div
                      style={{
                        marginTop: 10,
                        color: "#b00020",
                        fontSize: 13,
                        fontWeight: 500,
                        fontFamily: "Roboto",
                      }}
                    >
                      {countryDetailsError}
                    </div>
                  )}
                </div>
              )}

              {shouldShowCountryDetails && (
                <div className="otModalSection">
                  <div className="otModalSectionTitle">
                    <span className="otPill otPillAccent">
                      {shouldShowIndividualCountryFields
                        ? "Country details"
                        : "Incorporation country"}
                    </span>
                  </div>

                  <div
                    className={
                      shouldShowIndividualCountryFields
                        ? "otGrid2Equal otIndividualCountryGrid"
                        : "otGrid2a"
                    }
                    style={{ alignItems: "start" }}
                  >
                    {shouldShowIndividualCountryFields ? (
                      <>
                        <div data-ot-entry-field="nationality">
                          <div className="otFieldLabel">Nationality</div>
                          <CountrySelect
                            value={detailsNationality}
                            onChange={(value) => {
                              handleSimpleDetailsCountryChange(
                                value,
                                setDetailsNationality
                              );
                            }}
                            className="otCountrySelect"
                            optionTextClassName="otCountryOptionText"
                            menuMinWidth={220}
                            menuPortalZIndex={100010}
                            optionFontSize={13}
                            placeholder="Select nationality"
                          />
                        </div>
                        <div data-ot-entry-field="domicile">
                          <div className="otFieldLabel">Domicile</div>
                          <CountrySelect
                            value={detailsDomicile}
                            onChange={(value) => {
                              handleSimpleDetailsCountryChange(
                                value,
                                setDetailsDomicile
                              );
                            }}
                            className="otCountrySelect"
                            optionTextClassName="otCountryOptionText"
                            menuMinWidth={220}
                            menuPortalZIndex={100010}
                            optionFontSize={13}
                            placeholder="Select domicile"
                          />
                        </div>
                      </>
                    ) : (
                      <div data-ot-entry-field="incorporation">
                        <div className="otFieldLabel">
                          Country of incorporation
                        </div>
                        <CountrySelect
                          value={detailsIncorporationCountry}
                          onChange={(value) => {
                            handleSimpleDetailsCountryChange(
                              value,
                              setDetailsIncorporationCountry
                            );
                          }}
                          className="otCountrySelect"
                          optionTextClassName="otCountryOptionText"
                          menuMinWidth={220}
                          menuPortalZIndex={100010}
                          optionFontSize={13}
                          placeholder="Select country"
                        />
                      </div>
                    )}
                  </div>
                  {!!countryDetailsError && (
                    <div
                      style={{
                        marginTop: 10,
                        color: "#b00020",
                        fontSize: 13,
                        fontWeight: 500,
                        fontFamily: "Roboto",
                      }}
                    >
                      {countryDetailsError}
                    </div>
                  )}
                </div>
              )}

              {/* {detailsOpen && (
                <pre style={{ fontSize: 12, opacity: 0.7 }}>
                  {JSON.stringify(
                    {
                      pendingType,
                      isAccountHolder: !!pendingAddRef.current?.isAccountHolder,
                      draftId: pendingAddRef.current?.draftId,
                    },
                    null,
                    2
                  )}
                </pre>
              )} */}

              {/* --- Holding classification --- */}
              {pendingType === "Holding" && (
                <div className="otModalSection">
                  <div className="otModalSectionTitle">
                    <span className="otPill otPillAccent">Holding type</span>
                  </div>

                  <div className="otHint" style={{ marginTop: 8 }}>
                    Choose the category that best matches the holding’s primary
                    purpose.
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 10,
                      marginTop: 10,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setHoldingChoice("active");
                        setHoldingTouched(false);
                      }}
                      style={{
                        textAlign: "left",
                        borderRadius: 14,
                        padding: "10px 12px",
                        border:
                          holdingChoice === "active"
                            ? "1.5px solid #BBC8D3"
                            : "1px solid rgba(0,0,0,0.12)",
                        background:
                          holdingChoice === "active" ? "#F0F1F3" : "#fff",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 500,
                          fontFamily: "Roboto",
                          color: "#515662",
                        }}
                      >
                        Active holding
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          opacity: 0.8,
                          marginTop: 6,
                          lineHeight: 1.35,
                          color: "#595C63",
                        }}
                      >
                        Holds directly or indirectly a <b>majority stake</b> in
                        one or more <b>operating companies</b>. Main purpose is{" "}
                        <b>not</b> the administration of third-party assets.
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setHoldingChoice("passive");
                        setHoldingTouched(false);
                      }}
                      style={{
                        textAlign: "left",
                        borderRadius: 14,
                        padding: "10px 12px",
                        border:
                          holdingChoice === "passive"
                            ? "1.5px solid #BBC8D3"
                            : "1px solid rgba(0,0,0,0.12)",
                        background:
                          holdingChoice === "passive" ? "#F0F1F3" : "#fff",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 500,
                          fontFamily: "Roboto",
                          color: "#515662",
                        }}
                      >
                        Passive holding
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          opacity: 0.8,
                          marginTop: 6,
                          lineHeight: 1.35,
                          color: "#595C63",
                        }}
                      >
                        Mainly <b>combines and manages assets</b> of a{" "}
                        <b>family</b> or a defined group. Often used to{" "}
                        <b>enable dividend distributions</b>.
                      </div>
                    </button>
                  </div>

                  {holdingTouched && !holdingChoice && (
                    <div
                      style={{
                        fontFamily: "Roboto",
                        marginTop: 10,
                        color: "#b00020",
                        fontSize: 13,
                        fontWeight: 500,
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      Please select Active or Passive holding
                    </div>
                  )}
                </div>
              )}

              {/* --- Account-holder trust/foundation name handling --- */}
              {pendingAddRef.current?.isAccountHolder &&
                ((pendingType === "Trust" && isAccountHolderTrustQuestionStep) ||
                  pendingType === "Foundation") && (
                  <div className="otModalSection">
                    <div className="otModalSectionTitle">
                      <span className="otPill otPillAccent">
                        {pendingType === "Trust"
                          ? "Client name"
                          : "Foundation name"}
                      </span>
                    </div>

                    <div
                      style={{
                        marginTop: 10,
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: "#F6F7F9",
                        border: "1px solid rgba(0,0,0,0.08)",
                        fontFamily: "Roboto",
                        fontWeight: 600,
                        color: "#4A4F59",
                      }}
                    >
                      {trimmedCustomerName || "(unnamed)"}
                    </div>

                    {pendingType === "Trust" ? (
                      <AnimatePresence mode="wait" initial={false}>
                        {isAccountHolderTrustQuestionStep ? (
                          <motion.div
                            key="trust-name-question"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                            style={{ marginTop: 12 }}
                          >
                            <div
                              className="otFieldLabel"
                              style={{ marginBottom: 8 }}
                            >
                              Does this current client name refer to the trust
                              or to the trustee?
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() =>
                                  setAccountHolderTrustNameKind("trustee")
                                }
                              >
                                This is trustee name
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() =>
                                  setAccountHolderTrustNameKind("trust")
                                }
                              >
                                This is trust name
                              </button>
                            </div>

                            {submitAttempted ? (
                              <div
                                style={{
                                  marginTop: 8,
                                  color: "#b00020",
                                  fontSize: 13,
                                  fontWeight: 500,
                                }}
                              >
                                Please select whether the current client name is
                                the trust name or the trustee name.
                              </div>
                            ) : null}
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    ) : null}
                  </div>
                )}

              {pendingType === "Trust" &&
              (!pendingAddRef.current?.isAccountHolder ||
                accountHolderTrustNameKind === "trust" ||
                accountHolderTrustNameKind === "trustee") ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                >
                  {pendingAddRef.current?.isAccountHolder &&
                  accountHolderTrustNameKind === "trust" ? (
                    <div className="otModalSection">
                      <div className="otModalSectionTitle">
                        <span className="otPill otPillAccent">Trust name</span>
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          padding: "10px 12px",
                          borderRadius: 12,
                          background: "#F6F7F9",
                          border: "1px solid rgba(0,0,0,0.08)",
                          fontFamily: "Roboto",
                          fontWeight: 600,
                          color: "#4A4F59",
                        }}
                      >
                        {trimmedCustomerName || "(unnamed)"}
                      </div>
                    </div>
                  ) : null}

                  {pendingAddRef.current?.isAccountHolder &&
                  accountHolderTrustNameKind === "trustee" ? (
                    <div className="otModalSection">
                      <div className="otModalSectionTitle">
                        <span className="otPill otPillAccent">Trust name</span>
                      </div>
                      <input
                        className="otInput"
                        value={detailsName}
                        onChange={(e) => {
                          setDetailsName(e.target.value);
                          if (nameError) setNameError("");
                        }}
                        placeholder="e.g. AAAB Trust"
                      />
                      {submitAttempted && !!nameError ? (
                        <div
                          style={{
                            marginTop: 8,
                            color: "#b00020",
                            fontSize: 13,
                            fontWeight: 500,
                            fontFamily: "Roboto",
                          }}
                        >
                          {nameError}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="otModalSection">
                    <div className="otModalSectionTitle">
                      <span className="otPill otPillAccent">Trust roles</span>
                    </div>

                    <div className="otTrustRoleBlock">
                      <div className="otRoleCountryGrid">
                      <div>
                        <div className="otFieldLabel">Settlor (name)</div>
                        {separateNames ? (
                          <IndividualNamePreview inputClass="otInput" displayName={trustRoles.settlorName} value={trustRoles.settlorNameParts || {}} onChange={(patch) => setTrustRoles(p => ({ ...p, settlorName: patch.name, settlorNameParts: nameFields(patch) }))} framed />
                        ) : (
                        <input
                          className="otInput"
                          value={trustRoles.settlorName}
                          onChange={(e) =>
                            setTrustRoles((p) => ({
                              ...p,
                              settlorName: e.target.value,
                            }))
                          }
                          placeholder="e.g. John Smith"
                        />
                        )}
                      </div>
                      {OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED && (
                        <>
                          <div className="otRoleCountryCompactField">
                            <div className="otFieldLabel otRoleCountryLabel">
                              Nationality
                            </div>
                            <CountrySelect
                              value={trustRoles.settlorNationality}
                              onChange={(value) =>
                                setTrustRoles((p) => ({
                                  ...p,
                                  settlorNationality: value,
                                }))
                              }
                              className="otCountrySelect"
                              optionTextClassName="otCountryOptionText"
                              singleValueMode="iso3"
                              menuMinWidth={220}
                              menuPortalZIndex={100010}
                              optionFontSize={13}
                              placeholder="Select"
                            />
                          </div>
                          <div className="otRoleCountryCompactField">
                            <div className="otFieldLabel otRoleCountryLabel">
                              Domicile
                            </div>
                            <CountrySelect
                              value={trustRoles.settlorDomicile}
                              onChange={(value) =>
                                setTrustRoles((p) => ({
                                  ...p,
                                  settlorDomicile: value,
                                }))
                              }
                              className="otCountrySelect"
                              optionTextClassName="otCountryOptionText"
                              singleValueMode="iso3"
                              menuMinWidth={220}
                              menuPortalZIndex={100010}
                              optionFontSize={13}
                              placeholder="Select"
                            />
                          </div>
                        </>
                      )}
                      </div>
                    </div>

                    <div className="otTrustRoleBlock otTrustRoleBlockSeparated">
                      <div
                        className={
                          trustRoles.trusteeKind === "company"
                            ? "otTrusteeRowGrid otTrusteeRowGridCorporate"
                            : "otTrusteeRowGrid"
                        }
                        style={{ alignItems: "end" }}
                      >
                    <div className="otTrusteeTypeField">
                      <div className="otFieldLabel">Trustee type</div>
                      {pendingAddRef.current?.isAccountHolder &&
                      accountHolderTrustNameKind === "trustee" ? (
                        <div
                          style={{
                            marginTop: 8,
                            padding: "10px 12px",
                            borderRadius: 12,
                            background: "#F6F7F9",
                            border: "1px solid rgba(0,0,0,0.08)",
                            fontFamily: "Roboto",
                            fontWeight: 600,
                            color: "#4A4F59",
                          }}
                        >
                          Corporate
                        </div>
                      ) : (
                        <select
                          className="otSelect"
                          style={{ fontFamily: "Roboto" }}
                          value={trustRoles.trusteeKind}
                          onChange={(e) =>
                            setTrustRoles((p) => ({
                              ...p,
                              trusteeKind:
                                e.target.value === "company"
                                  ? "company"
                                  : "individual",
                              trusteeCountry: "",
                              trusteeNationality: "",
                              trusteeDomicile: "",
                            }))
                          }
                        >
                          <option value="company">Corporate</option>
                          <option value="individual">Individual</option>
                        </select>
                      )}
                    </div>

                      <div>
                        <div className="otFieldLabel">Trustee name</div>
                        {pendingAddRef.current?.isAccountHolder &&
                        accountHolderTrustNameKind === "trustee" ? (
                          <div
                            style={{
                              marginTop: 8,
                              padding: "10px 12px",
                              borderRadius: 12,
                              background: "#F6F7F9",
                              border: "1px solid rgba(0,0,0,0.08)",
                              fontFamily: "Roboto",
                              fontWeight: 600,
                              color: "#4A4F59",
                            }}
                          >
                            {trimmedCustomerName || "(unnamed)"}
                          </div>
                        ) : separateNames && trustRoles.trusteeKind === "individual" ? (
                          <IndividualNamePreview inputClass="otInput" displayName={trustRoles.trusteeName} value={trustRoles.trusteeNameParts || {}} onChange={(patch) => setTrustRoles(p => ({ ...p, trusteeName: patch.name, trusteeNameParts: nameFields(patch) }))} framed />
                        ) : (
                          <input
                            className="otInput"
                            value={trustRoles.trusteeName}
                            onChange={(e) =>
                              setTrustRoles((p) => ({
                                ...p,
                                trusteeName: e.target.value,
                              }))
                            }
                            placeholder={
                              trustRoles.trusteeKind === "company"
                                ? "e.g. ABC Trustees Ltd"
                                : "e.g. Jane Smith"
                            }
                          />
                        )}
                      </div>
                      {OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED &&
                        (trustRoles.trusteeKind === "company" ? (
                          <div className="otRoleCountryCompactField">
                            <div className="otFieldLabel otRoleCountryLabel">
                              Country
                            </div>
                            <CountrySelect
                              value={trustRoles.trusteeCountry}
                              onChange={(value) =>
                                setTrustRoles((p) => ({
                                  ...p,
                                  trusteeCountry: value,
                                }))
                              }
                              className="otCountrySelect"
                              optionTextClassName="otCountryOptionText"
                              singleValueMode="iso3"
                              menuMinWidth={220}
                              menuPortalZIndex={100010}
                              optionFontSize={13}
                              placeholder="Select"
                            />
                          </div>
                        ) : (
                          <>
                            <div className="otRoleCountryCompactField">
                              <div className="otFieldLabel otRoleCountryLabel">
                                Nationality
                              </div>
                              <CountrySelect
                                value={trustRoles.trusteeNationality}
                                onChange={(value) =>
                                  setTrustRoles((p) => ({
                                    ...p,
                                    trusteeNationality: value,
                                  }))
                                }
                                className="otCountrySelect"
                                optionTextClassName="otCountryOptionText"
                                singleValueMode="iso3"
                                menuMinWidth={220}
                                menuPortalZIndex={100010}
                                optionFontSize={13}
                                placeholder="Select"
                              />
                            </div>
                            <div className="otRoleCountryCompactField">
                              <div className="otFieldLabel otRoleCountryLabel">
                                Domicile
                              </div>
                              <CountrySelect
                                value={trustRoles.trusteeDomicile}
                                onChange={(value) =>
                                  setTrustRoles((p) => ({
                                    ...p,
                                    trusteeDomicile: value,
                                  }))
                                }
                                className="otCountrySelect"
                                optionTextClassName="otCountryOptionText"
                                singleValueMode="iso3"
                                menuMinWidth={220}
                                menuPortalZIndex={100010}
                                optionFontSize={13}
                                placeholder="Select"
                              />
                            </div>
                          </>
                        ))}
                      </div>
                    </div>

                    <div className="otTrustRoleBlock otTrustRoleBlockSeparated otTrustBeneficiaryBlock">
                      {!trustRoles.beneficiariesAreClass && (
                        <div>
                          <div
                            className={`otBeneficiaryCountryGrid otBeneficiaryHeaderGrid ${
                              OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED
                                ? "otBeneficiaryCountryGridWithCountries"
                                : "otBeneficiaryCountryGridNameOnly"
                            }`}
                          >
                            <div className="otFieldLabel">
                              Beneficiaries (names)
                            </div>
                            {OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED && (
                              <>
                                <div className="otRoleCountryCompactField">
                                  <div className="otFieldLabel otRoleCountryLabel">
                                    Nationality
                                  </div>
                                </div>
                                <div className="otRoleCountryCompactField">
                                  <div className="otFieldLabel otRoleCountryLabel">
                                    Domicile
                                  </div>
                                </div>
                              </>
                            )}
                            <span
                              className="otBeneficiaryRemoveHeaderSpacer"
                              aria-hidden="true"
                            />
                          </div>

                          <div style={{ display: "grid", gap: 8 }}>
                            {(trustRoles.beneficiaries || [""]).map(
                              (bn, idx) => {
                                const beneficiary =
                                  normalizeTrustBeneficiary(bn);
                                return (
                                  <div
                                    key={`tb-${idx}`}
                                    className={`otBeneficiaryCountryGrid ${
                                      OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED
                                        ? "otBeneficiaryCountryGridWithCountries"
                                        : "otBeneficiaryCountryGridNameOnly"
                                    }`}
                                  >
                                    {separateNames ? (
                                      <IndividualNamePreview inputClass="otInput" displayName={beneficiary.name} value={beneficiary} onChange={(patch) => updateTrustBeneficiary(idx, patch)} framed />
                                    ) : (
                                    <input
                                      className="otInput"
                                      value={beneficiary.name}
                                      onChange={(e) =>
                                        updateTrustBeneficiary(
                                          idx,
                                          "name",
                                          e.target.value
                                        )
                                      }
                                      placeholder={`Beneficiary ${idx + 1}`}
                                    />
                                    )}
                                    {OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED && (
                                      <>
                                        <div className="otRoleCountryCompactField">
                                          <CountrySelect
                                            value={beneficiary.nationality}
                                            onChange={(value) =>
                                              updateTrustBeneficiary(
                                                idx,
                                                "nationality",
                                                value
                                              )
                                            }
                                            className="otCountrySelect"
                                            optionTextClassName="otCountryOptionText"
                                            singleValueMode="iso3"
                                            menuMinWidth={220}
                                            menuPortalZIndex={100010}
                                            optionFontSize={13}
                                            placeholder="Select"
                                          />
                                        </div>
                                        <div className="otRoleCountryCompactField">
                                          <CountrySelect
                                            value={beneficiary.domicile}
                                            onChange={(value) =>
                                              updateTrustBeneficiary(
                                                idx,
                                                "domicile",
                                                value
                                              )
                                            }
                                            className="otCountrySelect"
                                            optionTextClassName="otCountryOptionText"
                                            singleValueMode="iso3"
                                            menuMinWidth={220}
                                            menuPortalZIndex={100010}
                                            optionFontSize={13}
                                            placeholder="Select"
                                          />
                                        </div>
                                      </>
                                    )}
                                    <button
                                      type="button"
                                      className="otSecondaryBtn"
                                      onClick={() =>
                                        removeTrustBeneficiaryRow(idx)
                                      }
                                      style={{ padding: "8px 10px" }}
                                      title="Remove"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                );
                              }
                            )}
                          </div>

                        </div>
                      )}
                      <div className="otBeneficiaryActions">
                        <button
                          type="button"
                          className="otSecondaryBtn"
                          onClick={addTrustBeneficiaryRow}
                          disabled={trustRoles.beneficiariesAreClass}
                        >
                          + Add beneficiary
                        </button>
                        <label
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            fontFamily: "Roboto",
                            fontSize: 13,
                            color: "#5E7894",
                          }}
                        >
                          <input
                            className="otMutedCheckbox"
                            type="checkbox"
                            checked={trustRoles.beneficiariesAreClass}
                            onChange={(e) =>
                              setTrustRoles((p) => ({
                                ...p,
                                beneficiariesAreClass: !!e.target.checked,
                              }))
                            }
                          />
                          Beneficiaries are a class (no names)
                        </label>
                      </div>
                    </div>

                    <div className="otTrustRoleBlock otTrustRoleBlockSeparated">
                      <label
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          fontFamily: "Roboto",
                          fontSize: 13,
                        }}
                      >
                        <input
                          className="otMutedCheckbox"
                          type="checkbox"
                          checked={trustRoles.hasProtector}
                          onChange={(e) =>
                            setTrustRoles((p) => ({
                              ...p,
                              hasProtector: !!e.target.checked,
                            }))
                          }
                        />
                        Protector (optional)
                      </label>

                      {trustRoles.hasProtector && (
                        <div
                          className={
                            trustRoles.protectorKind === "company"
                              ? "otTrusteeRowGrid otTrusteeRowGridCorporate"
                              : "otTrusteeRowGrid"
                          }
                          style={{ marginTop: 10, alignItems: "end" }}
                        >
                          <div className="otTrusteeTypeField">
                            <div className="otFieldLabel">Protector type</div>
                            <select
                              className="otSelect"
                              value={trustRoles.protectorKind}
                              onChange={(e) =>
                                setTrustRoles((p) => ({
                                  ...p,
                                  protectorKind:
                                    e.target.value === "company"
                                      ? "company"
                                      : "individual",
                                  protectorCountry: "",
                                  protectorNationality: "",
                                  protectorDomicile: "",
                                }))
                              }
                            >
                              <option value="individual">Individual</option>
                              <option value="company">Corporate</option>
                            </select>
                          </div>

                          <div>
                            <div className="otFieldLabel">Protector name</div>
                            {separateNames && trustRoles.protectorKind === "individual" ? (
                              <IndividualNamePreview inputClass="otInput" displayName={trustRoles.protectorName} value={trustRoles.protectorNameParts || {}} onChange={(patch) => setTrustRoles(p => ({ ...p, protectorName: patch.name, protectorNameParts: nameFields(patch) }))} framed />
                            ) : (
                            <input
                              className="otInput"
                              value={trustRoles.protectorName}
                              onChange={(e) =>
                                setTrustRoles((p) => ({
                                  ...p,
                                  protectorName: e.target.value,
                                }))
                              }
                              placeholder={
                                trustRoles.protectorKind === "company"
                                  ? "e.g. Protector Co Ltd"
                                  : "e.g. Michael Brown"
                              }
                            />
                            )}
                          </div>
                          {OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED &&
                            (trustRoles.protectorKind === "company" ? (
                              <div className="otRoleCountryCompactField">
                                <div className="otFieldLabel otRoleCountryLabel">
                                  Country
                                </div>
                                <CountrySelect
                                  value={trustRoles.protectorCountry}
                                  onChange={(value) =>
                                    setTrustRoles((p) => ({
                                      ...p,
                                      protectorCountry: value,
                                    }))
                                  }
                                  className="otCountrySelect"
                                  optionTextClassName="otCountryOptionText"
                                  singleValueMode="iso3"
                                  menuMinWidth={220}
                                  menuPortalZIndex={100010}
                                  optionFontSize={13}
                                  placeholder="Select"
                                />
                              </div>
                            ) : (
                              <>
                                <div className="otRoleCountryCompactField">
                                  <div className="otFieldLabel otRoleCountryLabel">
                                    Nationality
                                  </div>
                                  <CountrySelect
                                    value={trustRoles.protectorNationality}
                                    onChange={(value) =>
                                      setTrustRoles((p) => ({
                                        ...p,
                                        protectorNationality: value,
                                      }))
                                    }
                                    className="otCountrySelect"
                                    optionTextClassName="otCountryOptionText"
                                    singleValueMode="iso3"
                                    menuMinWidth={220}
                                    menuPortalZIndex={100010}
                                    optionFontSize={13}
                                    placeholder="Select"
                                  />
                                </div>
                                <div className="otRoleCountryCompactField">
                                  <div className="otFieldLabel otRoleCountryLabel">
                                    Domicile
                                  </div>
                                  <CountrySelect
                                    value={trustRoles.protectorDomicile}
                                    onChange={(value) =>
                                      setTrustRoles((p) => ({
                                        ...p,
                                        protectorDomicile: value,
                                      }))
                                    }
                                    className="otCountrySelect"
                                    optionTextClassName="otCountryOptionText"
                                    singleValueMode="iso3"
                                    menuMinWidth={220}
                                    menuPortalZIndex={100010}
                                    optionFontSize={13}
                                    placeholder="Select"
                                  />
                                </div>
                              </>
                            ))}
                        </div>
                      )}
                    </div>

                    {submitAttempted && !!trustValidationMsg && (
                      <div
                        style={{
                          marginTop: 12,
                          color: "#b00020",
                          fontSize: 13,
                          fontWeight: 500,
                          fontFamily: "Roboto",
                        }}
                      >
                        {trustValidationMsg}
                      </div>
                    )}
                  </div>
                </motion.div>
              ) : null}

              {/* --- Foundation roles --- */}
              {pendingType === "Foundation" && (
                <div className="otModalSection">
                  <div className="otModalSectionTitle">
                    <span className="otPill otPillAccent">
                      Foundation roles
                    </span>
                  </div>

                  <div className="otTrustRoleBlock">
                    <div className="otRoleCountryGrid">
                      <div>
                        <div className="otFieldLabel">Founder</div>
                        {separateNames ? (
                          <IndividualNamePreview inputClass="otInput" displayName={foundationRoles.founderName} value={foundationRoles.founderNameParts || {}} onChange={(patch) => setFoundationRoles(p => ({ ...p, founderName: patch.name, founderNameParts: nameFields(patch) }))} framed />
                        ) : (
                        <input
                          className="otInput"
                          value={foundationRoles.founderName}
                          onChange={(e) =>
                            setFoundationRoles((p) => ({
                              ...p,
                              founderName: e.target.value,
                            }))
                          }
                          placeholder="e.g. John Smith"
                        />
                        )}
                      </div>
                      {OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED && (
                        <>
                          <div className="otRoleCountryCompactField">
                            <div className="otFieldLabel otRoleCountryLabel">
                              Nationality
                            </div>
                            <CountrySelect
                              value={foundationRoles.founderNationality}
                              onChange={(value) =>
                                setFoundationRoles((p) => ({
                                  ...p,
                                  founderNationality: value,
                                }))
                              }
                              className="otCountrySelect"
                              optionTextClassName="otCountryOptionText"
                              singleValueMode="iso3"
                              menuMinWidth={220}
                              menuPortalZIndex={100010}
                              optionFontSize={13}
                              placeholder="Select"
                            />
                          </div>
                          <div className="otRoleCountryCompactField">
                            <div className="otFieldLabel otRoleCountryLabel">
                              Domicile
                            </div>
                            <CountrySelect
                              value={foundationRoles.founderDomicile}
                              onChange={(value) =>
                                setFoundationRoles((p) => ({
                                  ...p,
                                  founderDomicile: value,
                                }))
                              }
                              className="otCountrySelect"
                              optionTextClassName="otCountryOptionText"
                              singleValueMode="iso3"
                              menuMinWidth={220}
                              menuPortalZIndex={100010}
                              optionFontSize={13}
                              placeholder="Select"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="otTrustRoleBlock otTrustRoleBlockSeparated">
                    <div
                      className={
                        foundationRoles.councilKind === "company"
                          ? "otTrusteeRowGrid otTrusteeRowGridCorporate"
                          : "otTrusteeRowGrid"
                      }
                      style={{ alignItems: "end" }}
                    >
                      <div className="otTrusteeTypeField">
                        <div className="otFieldLabel">Council/Board type</div>
                        <select
                          className="otSelect"
                          style={{ fontFamily: "Roboto" }}
                          value={foundationRoles.councilKind}
                          onChange={(e) =>
                            setFoundationRoles((p) => ({
                              ...p,
                              councilKind:
                                e.target.value === "company"
                                  ? "company"
                                  : "individual",
                              councilCountry: "",
                              councilNationality: "",
                              councilDomicile: "",
                            }))
                          }
                        >
                          <option value="company">Corporate</option>
                          <option value="individual">Individual</option>
                        </select>
                      </div>

                      <div>
                        <div className="otFieldLabel">Council/Board name</div>
                        {separateNames && foundationRoles.councilKind === "individual" ? (
                          <IndividualNamePreview inputClass="otInput" displayName={foundationRoles.councilName} value={foundationRoles.councilNameParts || {}} onChange={(patch) => setFoundationRoles(p => ({ ...p, councilName: patch.name, councilNameParts: nameFields(patch) }))} framed />
                        ) : (
                        <input
                          className="otInput"
                          value={foundationRoles.councilName}
                          onChange={(e) =>
                            setFoundationRoles((p) => ({
                              ...p,
                              councilName: e.target.value,
                            }))
                          }
                          placeholder={
                            foundationRoles.councilKind === "company"
                              ? "e.g. ABC Council Services Ltd"
                              : "e.g. Jane Smith"
                          }
                        />
                        )}
                      </div>
                      {OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED &&
                        (foundationRoles.councilKind === "company" ? (
                          <div className="otRoleCountryCompactField">
                            <div className="otFieldLabel otRoleCountryLabel">
                              Country
                            </div>
                            <CountrySelect
                              value={foundationRoles.councilCountry}
                              onChange={(value) =>
                                setFoundationRoles((p) => ({
                                  ...p,
                                  councilCountry: value,
                                }))
                              }
                              className="otCountrySelect"
                              optionTextClassName="otCountryOptionText"
                              singleValueMode="iso3"
                              menuMinWidth={220}
                              menuPortalZIndex={100010}
                              optionFontSize={13}
                              placeholder="Select"
                            />
                          </div>
                        ) : (
                          <>
                            <div className="otRoleCountryCompactField">
                              <div className="otFieldLabel otRoleCountryLabel">
                                Nationality
                              </div>
                              <CountrySelect
                                value={foundationRoles.councilNationality}
                                onChange={(value) =>
                                  setFoundationRoles((p) => ({
                                    ...p,
                                    councilNationality: value,
                                  }))
                                }
                                className="otCountrySelect"
                                optionTextClassName="otCountryOptionText"
                                singleValueMode="iso3"
                                menuMinWidth={220}
                                menuPortalZIndex={100010}
                                optionFontSize={13}
                                placeholder="Select"
                              />
                            </div>
                            <div className="otRoleCountryCompactField">
                              <div className="otFieldLabel otRoleCountryLabel">
                                Domicile
                              </div>
                              <CountrySelect
                                value={foundationRoles.councilDomicile}
                                onChange={(value) =>
                                  setFoundationRoles((p) => ({
                                    ...p,
                                    councilDomicile: value,
                                  }))
                                }
                                className="otCountrySelect"
                                optionTextClassName="otCountryOptionText"
                                singleValueMode="iso3"
                                menuMinWidth={220}
                                menuPortalZIndex={100010}
                                optionFontSize={13}
                                placeholder="Select"
                              />
                            </div>
                          </>
                        ))}
                    </div>
                  </div>

                  <div className="otTrustRoleBlock otTrustRoleBlockSeparated otTrustBeneficiaryBlock">
                    {!foundationRoles.beneficiariesAreClass && (
                      <div>
                        <div
                          className={`otBeneficiaryCountryGrid otBeneficiaryHeaderGrid ${
                            OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED
                              ? "otBeneficiaryCountryGridWithCountries"
                              : "otBeneficiaryCountryGridNameOnly"
                          }`}
                        >
                          <div className="otFieldLabel">
                            Beneficiaries (names)
                          </div>
                          {OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED && (
                            <>
                              <div className="otRoleCountryCompactField">
                                <div className="otFieldLabel otRoleCountryLabel">
                                  Nationality
                                </div>
                              </div>
                              <div className="otRoleCountryCompactField">
                                <div className="otFieldLabel otRoleCountryLabel">
                                  Domicile
                                </div>
                              </div>
                            </>
                          )}
                          <span
                            className="otBeneficiaryRemoveHeaderSpacer"
                            aria-hidden="true"
                          />
                        </div>

                        <div style={{ display: "grid", gap: 8 }}>
                          {(foundationRoles.beneficiaries || [""]).map(
                            (bn, idx) => {
                              const beneficiary =
                                normalizeFoundationBeneficiary(bn);
                              return (
                                <div
                                  key={`fb-${idx}`}
                                  className={`otBeneficiaryCountryGrid ${
                                    OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED
                                      ? "otBeneficiaryCountryGridWithCountries"
                                      : "otBeneficiaryCountryGridNameOnly"
                                  }`}
                                >
                                  {separateNames ? (
                                    <IndividualNamePreview inputClass="otInput" displayName={beneficiary.name} value={beneficiary} onChange={(patch) => updateFoundationBeneficiary(idx, patch)} framed />
                                  ) : (
                                  <input
                                    className="otInput"
                                    value={beneficiary.name}
                                    onChange={(e) =>
                                      updateFoundationBeneficiary(
                                        idx,
                                        "name",
                                        e.target.value
                                      )
                                    }
                                    placeholder={`Beneficiary ${idx + 1}`}
                                  />
                                  )}
                                  {OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED && (
                                    <>
                                      <div className="otRoleCountryCompactField">
                                        <CountrySelect
                                          value={beneficiary.nationality}
                                          onChange={(value) =>
                                            updateFoundationBeneficiary(
                                              idx,
                                              "nationality",
                                              value
                                            )
                                          }
                                          className="otCountrySelect"
                                          optionTextClassName="otCountryOptionText"
                                          singleValueMode="iso3"
                                          menuMinWidth={220}
                                          menuPortalZIndex={100010}
                                          optionFontSize={13}
                                          placeholder="Select"
                                        />
                                      </div>
                                      <div className="otRoleCountryCompactField">
                                        <CountrySelect
                                          value={beneficiary.domicile}
                                          onChange={(value) =>
                                            updateFoundationBeneficiary(
                                              idx,
                                              "domicile",
                                              value
                                            )
                                          }
                                          className="otCountrySelect"
                                          optionTextClassName="otCountryOptionText"
                                          singleValueMode="iso3"
                                          menuMinWidth={220}
                                          menuPortalZIndex={100010}
                                          optionFontSize={13}
                                          placeholder="Select"
                                        />
                                      </div>
                                    </>
                                  )}
                                  <button
                                    type="button"
                                    className="otSecondaryBtn"
                                    onClick={() =>
                                      removeFoundationBeneficiaryRow(idx)
                                    }
                                    style={{ padding: "8px 10px" }}
                                    title="Remove"
                                  >
                                    ✕
                                  </button>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    )}
                    <div className="otBeneficiaryActions">
                      <button
                        type="button"
                        className="otSecondaryBtn"
                        onClick={addFoundationBeneficiaryRow}
                        disabled={foundationRoles.beneficiariesAreClass}
                      >
                        + Add beneficiary
                      </button>
                      <label
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          fontFamily: "Roboto",
                          fontSize: 13,
                          color: "#5E7894",
                        }}
                      >
                        <input
                          className="otMutedCheckbox"
                          type="checkbox"
                          checked={foundationRoles.beneficiariesAreClass}
                          onChange={(e) =>
                            setFoundationRoles((p) => ({
                              ...p,
                              beneficiariesAreClass: !!e.target.checked,
                            }))
                          }
                        />
                        Beneficiaries are a class (no names)
                      </label>
                    </div>
                  </div>

                  <div className="otTrustRoleBlock otTrustRoleBlockSeparated">
                    <label
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        fontFamily: "Roboto",
                        fontSize: 13,
                      }}
                    >
                      <input
                        className="otMutedCheckbox"
                        type="checkbox"
                        checked={foundationRoles.hasGuardian}
                        onChange={(e) =>
                          setFoundationRoles((p) => ({
                            ...p,
                            hasGuardian: !!e.target.checked,
                          }))
                        }
                      />
                      Guardian (optional)
                    </label>

                    {foundationRoles.hasGuardian && (
                      <div
                        className={
                          foundationRoles.guardianKind === "company"
                            ? "otTrusteeRowGrid otTrusteeRowGridCorporate"
                            : "otTrusteeRowGrid"
                        }
                        style={{ marginTop: 10, alignItems: "end" }}
                      >
                        <div className="otTrusteeTypeField">
                          <div className="otFieldLabel">Guardian type</div>
                          <select
                            className="otSelect"
                            style={{ fontFamily: "Roboto" }}
                            value={foundationRoles.guardianKind}
                            onChange={(e) =>
                              setFoundationRoles((p) => ({
                                ...p,
                                guardianKind:
                                  e.target.value === "company"
                                    ? "company"
                                    : "individual",
                                guardianCountry: "",
                                guardianNationality: "",
                                guardianDomicile: "",
                              }))
                            }
                          >
                            <option value="individual">Individual</option>
                            <option value="company">Corporate</option>
                          </select>
                        </div>

                        <div>
                          <div className="otFieldLabel">Guardian name</div>
                          {separateNames && foundationRoles.guardianKind === "individual" ? (
                            <IndividualNamePreview inputClass="otInput" displayName={foundationRoles.guardianName} value={foundationRoles.guardianNameParts || {}} onChange={(patch) => setFoundationRoles(p => ({ ...p, guardianName: patch.name, guardianNameParts: nameFields(patch) }))} framed />
                          ) : (
                          <input
                            className="otInput"
                            value={foundationRoles.guardianName}
                            onChange={(e) =>
                              setFoundationRoles((p) => ({
                                ...p,
                                guardianName: e.target.value,
                              }))
                            }
                            placeholder={
                              foundationRoles.guardianKind === "company"
                                ? "e.g. Guardian Co Ltd"
                                : "e.g. Michael Brown"
                            }
                          />
                          )}
                        </div>
                        {OWNERSHIP_ASSISTANT_COUNTRY_DETAILS_ENABLED &&
                          (foundationRoles.guardianKind === "company" ? (
                            <div className="otRoleCountryCompactField">
                              <div className="otFieldLabel otRoleCountryLabel">
                                Country
                              </div>
                              <CountrySelect
                                value={foundationRoles.guardianCountry}
                                onChange={(value) =>
                                  setFoundationRoles((p) => ({
                                    ...p,
                                    guardianCountry: value,
                                  }))
                                }
                                className="otCountrySelect"
                                optionTextClassName="otCountryOptionText"
                                singleValueMode="iso3"
                                menuMinWidth={220}
                                menuPortalZIndex={100010}
                                optionFontSize={13}
                                placeholder="Select"
                              />
                            </div>
                          ) : (
                            <>
                              <div className="otRoleCountryCompactField">
                                <div className="otFieldLabel otRoleCountryLabel">
                                  Nationality
                                </div>
                                <CountrySelect
                                  value={foundationRoles.guardianNationality}
                                  onChange={(value) =>
                                    setFoundationRoles((p) => ({
                                      ...p,
                                      guardianNationality: value,
                                    }))
                                  }
                                  className="otCountrySelect"
                                  optionTextClassName="otCountryOptionText"
                                  singleValueMode="iso3"
                                  menuMinWidth={220}
                                  menuPortalZIndex={100010}
                                  optionFontSize={13}
                                  placeholder="Select"
                                />
                              </div>
                              <div className="otRoleCountryCompactField">
                                <div className="otFieldLabel otRoleCountryLabel">
                                  Domicile
                                </div>
                                <CountrySelect
                                  value={foundationRoles.guardianDomicile}
                                  onChange={(value) =>
                                    setFoundationRoles((p) => ({
                                      ...p,
                                      guardianDomicile: value,
                                    }))
                                  }
                                  className="otCountrySelect"
                                  optionTextClassName="otCountryOptionText"
                                  singleValueMode="iso3"
                                  menuMinWidth={220}
                                  menuPortalZIndex={100010}
                                  optionFontSize={13}
                                  placeholder="Select"
                                />
                              </div>
                            </>
                          ))}
                      </div>
                    )}
                  </div>

                  {submitAttempted && !!foundationValidationMsg && (
                    <div
                      style={{
                        marginTop: 12,
                        color: "#b00020",
                        fontSize: 13,
                        fontWeight: 500,
                        fontFamily: "Roboto",
                      }}
                    >
                      {foundationValidationMsg}
                    </div>
                  )}
                </div>
              )}

              {/* footer */}
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  justifyContent: "flex-end",
                  marginTop: 12,
                }}
              >
                <button
                  type="button"
                  className="otSecondaryBtn"
                  onClick={() => closeDetails("cancel")}
                >
                  Cancel
                </button>
                {!isAccountHolderTrustQuestionStep ? (
                  <button
                    type="button"
                    className={`otPrimaryBtn${
                      isSimpleDetailsComplete ? " otPrimaryBtnReady" : ""
                    }`}
                    onClick={() => {
                      setSubmitAttempted(true);
                      confirmDetails();
                    }}
                  >
                    Save
                  </button>
                ) : null}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* OTHER MODAL  */}
      <AnimatePresence>
        {SHOW_KYC_PREVIEW_CONFIRMATION && kycPreviewOpen && (
          <motion.div
            className="otModalOverlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            onMouseDown={() => setKycPreviewOpen(false)}
          >
            <motion.div
              className="otModalCard"
              initial={{ opacity: 0, y: 0, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.985 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{ maxWidth: 520, border: "none" }}
            >
              {!!kycPreviewErr && (
                <div
                  style={{
                    color: "#b00020",
                    fontFamily: "Roboto",
                    marginTop: 10,
                  }}
                >
                  {kycPreviewErr}
                </div>
              )}

              <div className="otModalHeader otModalPreviewHeader">
                <div className="otModalTitle otModalPreviewCenter">
                  Ownership & Control Preview
                </div>
              </div>

              <div className="otModalSubtitle otModalPreviewCenter otModalPreviewSubtitle">
                This will overwrite Direct Shareholders & Controlling Persons in
                the KYC form
              </div>

              <div style={{ marginTop: 15, lineHeight: 1.5 }}>
                <div
                  style={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    color: "#5F7080",
                    marginBottom: 6,
                  }}
                >
                  Direct shareholders
                </div>
                {kycPreviewPayload?.shareholders?.length ? (
                  <ul style={{ margin: "0 0 10px 18px" }}>
                    {kycPreviewPayload.shareholders.map((x) => (
                      <li key={x._rowId}>
                        {x.name || "(unnamed)"}

                        {x.shareholderEntityType &&
                        x.shareholderEntityType !== "Entity" ? (
                          <span style={{ opacity: 0.65 }}>
                            {" "}
                            — {x.shareholderEntityType}
                          </span>
                        ) : null}

                        {x.ownershipPercent ? (
                          <span style={{ opacity: 0.75 }}>
                            {" "}
                            ({x.ownershipPercent}%)
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul style={{ margin: "0 0 10px 18px" }}>
                    <li style={{ color: "#888C96" }}>
                      <div
                        style={{
                          opacity: 0.75,
                          marginBottom: 10,
                          color: "#888C96",
                        }}
                      >
                        None
                      </div>
                    </li>
                  </ul>
                )}

                <div
                  style={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    color: "#5F7080",
                    marginBottom: 6,
                  }}
                >
                  Controlling Persons (UBOs)
                </div>
                {kycPreviewPayload?.ubos?.length ? (
                  <ul style={{ margin: "0 0 0 18px" }}>
                    {kycPreviewPayload.ubos.map((x) => (
                      <li key={x._rowId}>
                        {x.name || "(unnamed)"}
                        {x.effectiveOwnershipPercent ? (
                          <span style={{ opacity: 0.75 }}>
                            {" "}
                            ({x.effectiveOwnershipPercent}%)
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul style={{ margin: "0 0 10px 18px" }}>
                    <li style={{ color: "#888C96" }}>
                      <div
                        style={{
                          opacity: 0.75,
                          marginBottom: 10,
                          color: "#888C96",
                        }}
                      >
                        None
                      </div>
                    </li>
                  </ul>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  justifyContent: "flex-end",
                  marginTop: 16,
                }}
              >
                <button
                  type="button"
                  className="otSecondaryBtn"
                  onClick={() => setKycPreviewOpen(false)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="otPrimaryBtn"
                  onClick={confirmKycPreviewUpdate}
                >
                  Confirm update
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* END other modal */}

      <AnimatePresence>
        {submitAlertOpen && (
          <motion.div
            className="otModalOverlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            onMouseDown={() => setSubmitAlertOpen(false)}
          >
            <motion.div
              className="otModalCard"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.985 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{ maxWidth: 520 }}
            >
              <div className="otModalHeader otModalPreviewHeader">
                <div className="otModalTitle otModalPreviewCenter">
                  Incomplete ownership tree
                </div>
              </div>

              <div
                className="otModalSubtitle otModalPreviewCenter otModalPreviewSubtitle"
                style={{ fontSize: 16 }}
              >
                {submitAlertMode === "noChildren"
                  ? "No ownership information added. Please add shareholding details by dragging relevant elements into the ownership tree, unless the account holder is a trust, a foundation, or a quoted company."
                  : endCount === 1
                  ? "One ownership chain ends on an entity that is not an ultimate beneficial owner (UBO). Please add the ownership details of this entity until the branch ends with an individual, a trust, a foundation, or a quoted company."
                  : "Some ownership chains end on entities that are not an ultimate beneficial owner (UBO). Please add the ownership details of those end entities until all branches end with an individual, a trust, a foundation, or a quoted company."}
              </div>

              {submitAlertMode === "invalidLeaves" && (
                <div style={{ marginTop: 14 }}>
                  <div
                    style={{
                      fontFamily: "Roboto",
                      fontWeight: 500,
                      color: "#5F7080",
                      marginBottom: 6,
                    }}
                  >
                    <span>
                      {endCount === 1
                        ? "Entity requiring ownership details (UBOs)"
                        : "Entities requiring ownership details (UBOs)"}
                    </span>
                  </div>

                  <ul style={{ margin: "0 0 0 18px" }}>
                    {submitAlertLeaves.map((x) => (
                      <li key={x.id}>
                        <span style={{ opacity: 0.85 }}>
                          {x.name} ({x.type})
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  justifyContent: "flex-end",
                  marginTop: 16,
                }}
              >
                <button
                  type="button"
                  className="otPrimaryBtn"
                  onClick={() => setSubmitAlertOpen(false)}
                >
                  OK
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---- END MODAL ---- */}
    </div>
  );
}

/* =========================================================
 * Read-only ownership tree renderer (for snapshot card)
 * ========================================================= */
function parseMermaidViewBox(svg) {
  const raw = String(svg?.getAttribute("viewBox") || "").trim();
  const parts = raw.split(/\s+/).map((value) => Number(value));
  if (parts.length !== 4 || parts.some((value) => !Number.isFinite(value))) {
    return null;
  }
  return {
    x: parts[0],
    y: parts[1],
    width: parts[2],
    height: parts[3],
  };
}

// Covers both Mermaid HTML-label mode (foreignObject children) and plain-label
// mode (SVG rect). Call after inserting SVG via innerHTML.
function applyNodeDimming(container, activeMermaidId) {
  if (!container) return;
  const allNodes = container.querySelectorAll("g.node");
  if (!activeMermaidId || allNodes.length <= 1) {
    allNodes.forEach((el) => { el.style.opacity = ""; });
    return;
  }
  allNodes.forEach((el) => {
    const id = String(el.getAttribute("id") || "");
    const isActive =
      id === `flowchart-${activeMermaidId}` ||
      id.startsWith(`flowchart-${activeMermaidId}-`);
    el.style.opacity = isActive ? "1" : "0.35";
  });
}

function applyMermaidEdgeLabelBg(container, bg) {
  if (!bg) return;
  // Plain-label mode: rect element carries the fill
  container.querySelectorAll(".edgeLabel rect").forEach((r) => {
    r.setAttribute("fill", bg);
    r.style.fill = bg;
  });
  // HTML-label mode: descendants of foreignObject are HTML elements
  container.querySelectorAll(".edgeLabel foreignObject *").forEach((d) => {
    if (d.namespaceURI === "http://www.w3.org/1999/xhtml" && d.style) {
      d.style.backgroundColor = bg;
      d.style.background = bg;
    }
  });
  // Also target .label / span.edgeLabel used by some Mermaid 11 builds
  container.querySelectorAll(".edgeLabel .label, .edgeLabel span").forEach((d) => {
    if (d.style) {
      d.style.backgroundColor = bg;
      d.style.background = bg;
    }
  });
}

export function OwnershipTreeUaeReadOnly({
  snapshot,
  validationWarning = "",
  invalidPercentageEdges,
  edgeLabelBg,
  highlightNodeId,
  onEditPercentage,
}) {
  const { ownershipDiagram } = useOwnershipLabels();
  const graphWrapRef = React.useRef(null);
  const editRef = React.useRef(onEditPercentage);
  const snapshotRef = React.useRef(snapshot);
  editRef.current = onEditPercentage;
  snapshotRef.current = snapshot;
  const percentagesEditable = typeof onEditPercentage === "function";
  const [err, setErr] = React.useState("");
  const [editErr, setEditErr] = React.useState("");
  const forceSafariHtmlLabels = isSafariBrowser();
  const snapshotLabelMode = React.useMemo(
    () => String(snapshot?.labelMode || "").trim(),
    [snapshot]
  );
  const effectiveLabelMode = forceSafariHtmlLabels
    ? "safariHtml"
    : snapshotLabelMode;
  const usesHtmlLabels = true;

  const rawMermaidSrc = React.useMemo(() => {
    const s = snapshot && typeof snapshot === "object" ? snapshot : {};
    const code = String(s?.mermaid || "").trim();
    const fallback = "flowchart TB\n  A[No ownership tree snapshot]";
    return code || fallback;
  }, [snapshot]);

  const isVisualBuilderSnapshot = React.useMemo(
    () =>
      String(snapshot?.source || "").trim() === "visualBuilder" ||
      /;\s*[A-Za-z_][\w-]*--/.test(rawMermaidSrc),
    [rawMermaidSrc, snapshot]
  );
  const isDesktopGuidedAssistantSnapshot =
    String(snapshot?.source || "").trim() === "desktopGuidedAssistant";
  const isManualEntrySnapshot =
    String(snapshot?.source || "").trim() === "manualEntry";
  const usesDesktopSummarySizing =
    isVisualBuilderSnapshot || isDesktopGuidedAssistantSnapshot || isManualEntrySnapshot;

  const mermaidSrc = React.useMemo(() => {
    const source = ownershipDiagram(rawMermaidSrc);
    if (effectiveLabelMode !== "safariHtml") return source;
    return convertHtmlTreeLabelsToSafariHtmlMermaid(source);
  }, [effectiveLabelMode, rawMermaidSrc, ownershipDiagram]);

  // A linear chain has no branching: no node is a source or target more than once.
  // Snapshots can be line-based (guided assistant) or semicolon-based (visual builder).
  const isLinearChain = React.useMemo(() => {
    const edgeRegex =
      /(?:^|[;\n])\s*([A-Za-z_][\w-]*)\s*--(?:>\|[^|]*\||\|[^|]*\|>|>)\s*([A-Za-z_][\w-]*)/g;
    const srcCounts = {};
    const tgtCounts = {};
    let edgeCount = 0;
    let m;
    while ((m = edgeRegex.exec(mermaidSrc)) !== null) {
      edgeCount += 1;
      srcCounts[m[1]] = (srcCounts[m[1]] || 0) + 1;
      tgtCounts[m[2]] = (tgtCounts[m[2]] || 0) + 1;
    }
    return (
      edgeCount > 0 &&
      Object.values(srcCounts).every((c) => c === 1) &&
      Object.values(tgtCounts).every((c) => c === 1)
    );
  }, [mermaidSrc]);

  const isRootOnlyDesktopGuidedSnapshot = React.useMemo(() => {
    if (!isDesktopGuidedAssistantSnapshot) return false;
    const edgeRegex =
      /(?:^|[;\n])\s*[A-Za-z_][\w-]*\s*--(?:>\|[^|]*\||\|[^|]*\|>|>)\s*[A-Za-z_][\w-]*/;
    if (edgeRegex.test(mermaidSrc)) return false;
    const nodeRegex = /(?:^|[;\n])\s*[A-Za-z_][\w-]*\s*(?:\(|\[|\{)/g;
    return Array.from(String(mermaidSrc || "").matchAll(nodeRegex)).length === 1;
  }, [isDesktopGuidedAssistantSnapshot, mermaidSrc]);

  const usesLinearSizing = isLinearChain || isRootOnlyDesktopGuidedSnapshot;

  React.useEffect(() => {
    try {
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: "loose",
        flowchart: {
          htmlLabels: usesHtmlLabels,
          wrappingWidth: 1000,
          useMaxWidth: true,
        },
      });
    } catch (_) {}
  }, [usesHtmlLabels]);

  // Render diagram whenever source changes
  React.useEffect(() => {
    let cancelled = false;

    async function run() {
      setErr("");
      setEditErr("");
      const el = graphWrapRef.current;
      if (!el) return;

      try {
        // Clear
        el.innerHTML = "";

        const id = `kyc_tree_ro_${Math.random().toString(36).slice(2, 9)}`;
        const res = await mermaid.render(id, mermaidSrc);
        if (cancelled) return;

        el.innerHTML = res.svg;

        // Use explicit edgeLabelBg prop when provided; otherwise walk up DOM.
        let containerBg = edgeLabelBg || "";
        if (!containerBg) {
          let bgAncestor = el.parentElement;
          while (bgAncestor && bgAncestor !== document.body) {
            const bg = getComputedStyle(bgAncestor).backgroundColor;
            if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") {
              containerBg = bg;
              break;
            }
            bgAncestor = bgAncestor.parentElement;
          }
        }
        applyMermaidEdgeLabelBg(el, containerBg);
        highlightInvalidOwnershipPercentages(el, invalidPercentageEdges);
        applyNodeDimming(el, highlightNodeId || "");
        if (percentagesEditable) {
          el.querySelectorAll('.edgeLabel [data-id]').forEach((label) => {
            if (!label.textContent.includes('%')) return;
            const id = label.getAttribute('data-id');
            const node = snapshotRef.current?.nodes?.find((candidate) => {
              const prefix = `L_${candidate.parentIDs?.[0]}_${candidate.ID || candidate.id}_`;
              return id.startsWith(prefix) && /^\d+$/.test(id.slice(prefix.length));
            });
            if (!node) return;
            const edge = { nodeId: String(node.ID || node.id), parentId: node.parentIDs[0] };
            bindInlineOwnershipPercentage(label, {
              name: node.name,
              getMaximum: () => maximumOwnershipPercentage(snapshotRef.current, edge),
              onChange: (value) => editRef.current?.(edge, value),
              onError: setEditErr,
            });
          });
          el.querySelectorAll('path.flowchart-link').forEach((path) => {
            const nodes = snapshotRef.current?.nodes || [];
            const node = nodes.find((candidate) => {
              const prefix = `L_${candidate.parentIDs?.[0]}_${candidate.ID || candidate.id}_`;
              return path.id.startsWith(prefix) && /^\d+$/.test(path.id.slice(prefix.length));
            });
            if (!node || (node.percent !== undefined && node.percent !== null && String(node.percent).trim() !== '')) return;
            const parent = nodes.find(candidate => (candidate.ID || candidate.id) === node.parentIDs?.[0]);
            if (!parent || parent.isPIC || parent.entityCategory === 'pic' ||
              !['Non Quoted Operational Company', 'Operating Company', 'HoldingActive', 'Holding (Active)'].includes(parent.type)) return;
            const edge = { nodeId: String(node.ID || node.id), parentId: node.parentIDs[0] };
            bindMissingOwnershipPercentage(path, {
              name: node.name,
              getMaximum: () => maximumOwnershipPercentage(snapshotRef.current, edge),
              onChange: value => editRef.current?.(edge, value),
              onError: setEditErr,
            }, containerBg);
          });
        }

        // Desktop generated snapshots should not fill a wide summary card just
        // because they are horizontally simple. The SVG itself is sized below
        // from its viewBox width.
        if (graphWrapRef.current) {
          graphWrapRef.current.style.maxWidth = usesDesktopSummarySizing
            ? "100%"
            : usesLinearSizing
            ? "60%"
            : "100%";
          graphWrapRef.current.style.margin = "0 auto";
          graphWrapRef.current.style.width = usesDesktopSummarySizing
            ? "100%"
            : "fit-content";
          graphWrapRef.current.style.minWidth =
            usesDesktopSummarySizing || usesLinearSizing ? "0" : "min(100%, 720px)";
        }
        const svg = graphWrapRef.current?.querySelector("svg");
        if (svg) {
          svg.style.width = usesLinearSizing ? "100%" : "auto";
          svg.style.maxWidth = "100%";
          svg.style.height = "auto";
          svg.style.display = "block";
          svg.style.margin = "0 auto";

          if (usesDesktopSummarySizing) {
            const viewBox = parseMermaidViewBox(svg);
            const availableWidth =
              graphWrapRef.current?.parentElement?.clientWidth ||
              graphWrapRef.current?.clientWidth ||
              0;

            if (viewBox?.width && availableWidth) {
              const naturalWidth = Math.round(viewBox.width);
              const visualBuilderSummaryScale = usesLinearSizing
                ? 0.7
                : 1;
              const fittedWidth = Math.min(naturalWidth, availableWidth);
              const minTargetWidth = usesLinearSizing
                ? 220
                : 280;
              const targetWidth = Math.max(
                minTargetWidth,
                Math.round(fittedWidth * visualBuilderSummaryScale)
              );
              svg.style.width = `${targetWidth}px`;
              svg.style.maxWidth = "100%";
            } else {
              svg.style.width = "65%";
              svg.style.maxWidth = "100%";
            }
          }
        }
      } catch (e) {
        if (cancelled) return;
        setErr(e?.message || "Failed to render ownership tree.");
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [
    percentagesEditable,
    invalidPercentageEdges,
    edgeLabelBg,
    highlightNodeId,
    isLinearChain,
    isRootOnlyDesktopGuidedSnapshot,
    isVisualBuilderSnapshot,
    mermaidSrc,
    usesLinearSizing,
    usesDesktopSummarySizing,
    usesHtmlLabels,
  ]);

  return (
    <div style={{ width: "100%" }}>
      {err || (editErr && !validationWarning) ? (
        <div role="alert" style={{ color: "#86123E", fontSize: 11, fontWeight: 500, textAlign: "right", marginBottom: 8 }}>
          {err || editErr}
        </div>
      ) : null}

      <div
        className={`otTreeAssistantSnapshotWrap${
          effectiveLabelMode === "safariHtml" ||
          effectiveLabelMode === "safariPlain"
            ? " otTreeAssistantSnapshotWrap--safariPlain"
            : ""
        }${usesDesktopSummarySizing ? " otTreeAssistantSnapshotWrap--compactVisualChain" : ""}`}
        style={{
          width: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          // borderRadius: 14,
          // border: "1px solid rgba(15,23,42,0.10)",
          // background: "#fff",
          // background: "#FCF9F7",
          // boxShadow: "0 10px 26px rgba(15,23,42,0.06)",
          // padding: 14,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div className="otTreeAssistantSnapshotGraph" ref={graphWrapRef} />
      </div>

      {/* <div
        style={{
          width: "100%",
          overflow: "auto",
          borderRadius: 14,
          border: "1px solid rgba(15,23,42,0.10)",
          background: "#fff",
          boxShadow: "0 10px 26px rgba(15,23,42,0.06)",
          padding: 14,
        }}
      >
        <div ref={graphWrapRef} />
      </div> */}
    </div>
  );
}
